import {
  computed,
  getCurrentScope,
  nextTick,
  onScopeDispose,
  ref,
  watch,
  type Ref,
  type ShallowRef,
} from 'vue'

import { usePendingRoutePointOps } from '@/composables/usePendingRoutePointOps'
import { useTrackDrag } from '@/composables/useTrackDrag'
import CONFIG from '@/services/configService'
import { showConfirm } from '@/services/dialogService'
import { generateId } from '@/services/idService'
import { buildLineStringFromSegments, parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  recalcSegmentsForDelete,
  recalcSegmentsForInsert,
  recalcSegmentsForMove,
  rebuildAllSegments,
  routeResultToSegmentWithTime,
} from '@/services/routeEditService'
import { registerRouteEditSessionHandler } from '@/services/routeEditSessionService'
import {
  abortPendingRouting,
  clearSpinningRoutePoints,
  routingService,
  setSpinningRoutePoints,
  spinningRoutePoint,
} from '@/services/routingService'
import { showError } from '@/services/toastService'
import { TrackColorService } from '@/services/trackColorService'
import type { TrackRoutingAdapter } from '@/services/trackRoutingAdapter'
import { useUiStore } from '@/stores/uiStore'
import type { RoutePoint, Segment, Tour, Track, Waypoint } from '@/types/tour'
import { refreshTourTrackStats, refreshTrackMetrics } from '@/utils/refreshTrackMetrics'
import {
  getRoutePointInsertOrderPlan,
  getRoutePointMultiInsertOrderPlan,
  getSparseTrackAppendOrder,
  getSparseTrackReorderPlan,
  getSparseTrackSeedOrders,
} from '@/utils/routePointOrder'
import {
  buildSparseTrackHelperCoords,
  DEFAULT_SPARSE_TRACK_MAX_HELPER_POINTS,
  getSuggestedSparseTrackHelperPointCount,
} from '@/utils/trackEditMaterialization'
import { getTrackLineInsertionProjection } from '@/utils/trackLineInsertion'
import {
  requireTrackClientId,
  getTrackGlobalInsertIndex,
  getTrackLocalIndexFromGlobalIndex,
  getTrackRoutePointEntries,
  getTrackRoutePoints,
} from '@/utils/trackRoutePoints'

import type maplibregl from 'maplibre-gl'
import type { MapMouseEvent } from 'maplibre-gl'

type DrawingHistoryEntry = {
  undo: () => Promise<void>
  redo: () => Promise<void>
}

type DrawingLocalState = {
  trackClientId: string | null
  segments: Segment[]
  lastClickedPoint: [number, number] | null
}

type RangeSegmentState = {
  kind: 'range'
  deletedIndices: number[]
  newSegments: { segment: Segment; order: number }[]
  lineString?: string
}

type FullSegmentState = {
  kind: 'full'
  segments: Segment[]
  lineString?: string
}

type SegmentState = RangeSegmentState | FullSegmentState

type EditHistoryEntry = {
  undo: () => Promise<void>
  redo: () => Promise<void>
}

type ProfileRerouteHistoryState = {
  previousProfile: string
  nextProfile: string
}

type SparseTrackPreviewPoint = {
  lat: number
  lon: number
}

type SparseTrackSetupState = {
  tourId: string
  trackClientId: string
  lineString: string
  pointCount: number
  minPointCount: number
  maxPointCount: number
  previewRoutePoints: SparseTrackPreviewPoint[]
}

type SparseTrackApplyState = {
  tourId: string
  trackClientId: string
  remainingPreviewRoutePoints: SparseTrackPreviewPoint[]
}

type RouteDraftMode = 'drawing' | 'edit'

type RouteDraftState = {
  mode: RouteDraftMode
  tourId: string
  trackClientId: string | null
  baselineTour: Tour
  draftTour: Tour
  dirty: boolean
}

export interface UseTrackRoutingSessionOptions {
  mapInstance: ShallowRef<maplibregl.Map | null>
  drawingTrackPoints: Ref<[number, number][]>
  drawingTrackSegments: Ref<Segment[]>
  lastClickedPoint: Ref<[number, number] | null>
  isCalculatingRoute: Ref<boolean>
  shouldSuppressInsertDragClick?: () => boolean
  adapter: TrackRoutingAdapter
}

const cloneSegment = (segment: Segment): Segment => JSON.parse(JSON.stringify(segment))
const cloneSegments = (segments: Segment[]): Segment[] => JSON.parse(JSON.stringify(segments))
const cloneRoutePoint = (routePoint: RoutePoint): RoutePoint =>
  JSON.parse(JSON.stringify(routePoint))
const cloneTour = (tour: Tour): Tour => JSON.parse(JSON.stringify(tour))
const cloneTrack = (track: Track): Track => JSON.parse(JSON.stringify(track))
const cloneRoutePoints = (routePoints: RoutePoint[]): RoutePoint[] =>
  JSON.parse(JSON.stringify(routePoints))
const cloneRangeSegments = (
  newSegments: { segment: Segment; order: number }[],
): { segment: Segment; order: number }[] =>
  newSegments.map(({ segment, order }) => ({
    segment: cloneSegment(segment),
    order,
  }))

const hasCompleteLocalSegments = (
  segments: Segment[],
  routePoints: Array<Pick<RoutePoint, 'lat' | 'lon'>>,
) =>
  segments.length === Math.max(0, routePoints.length - 1) &&
  segments.every((segment) => segment.points.length > 0)

const sameLngLat = (a: [number, number], b: [number, number]) =>
  Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9

/** Append the drop to `vertices`, skipping it when it already coincides with the last vertex. */
const appendDropCoord = (
  vertices: [number, number][],
  drop: [number, number],
): [number, number][] =>
  sameLngLat(vertices[vertices.length - 1], drop) ? [...vertices] : [...vertices, drop]

/** Prepend the drop to `vertices`, skipping it when it already coincides with the first vertex. */
const prependDropCoord = (
  vertices: [number, number][],
  drop: [number, number],
): [number, number][] => (sameLngLat(vertices[0], drop) ? [...vertices] : [drop, ...vertices])

const buildSegmentFromCoords = (coords: [number, number][]): Segment => {
  const timestamp = Date.now()
  return {
    clientId: generateId(),
    points: coords.map(([lon, lat]) => ({
      clientId: generateId(),
      lat,
      lon,
      time: timestamp,
    })),
  }
}

const sameRouteDraftValue = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null)

const getRouteDraftFingerprint = (tour: Tour, trackClientId: string | null) => {
  if (!trackClientId) return null
  const track = tour.tracks.find((candidate) => candidate.clientId === trackClientId) ?? null
  const routePoints = getTrackRoutePoints(tour.routePoints, trackClientId).map((routePoint) => ({
    clientId: routePoint.clientId,
    lat: routePoint.lat,
    lon: routePoint.lon,
    ele: routePoint.ele,
    name: routePoint.name,
    description: routePoint.description,
    order: routePoint.order,
    trackClientId: routePoint.trackClientId ?? null,
  }))

  return {
    track: track
      ? {
          clientId: track.clientId,
          name: track.name,
          color: track.color,
          type: track.type,
          visible: track.visible,
          pointsLineReduced5m: track.pointsLineReduced5m,
          segments: track.segments,
        }
      : null,
    routePoints,
  }
}

const getTrackInsertionLineString = (
  track: Pick<Tour['tracks'][number], 'pointsLineReduced5m' | 'segments'>,
) => track.pointsLineReduced5m ?? buildLineStringFromSegments(track.segments)

const addMinimumSavedSparseTrackAnchors = (
  track: Pick<Tour['tracks'][number], 'pointsLineReduced5m' | 'segments'>,
  trackClientId: string,
  routePoints: RoutePoint[],
): RoutePoint[] => {
  if (routePoints.length !== 2) return routePoints

  const helperCoords = buildSparseTrackHelperCoords(getTrackInsertionLineString(track), 1, 1)
  const helperCoord = helperCoords?.[0]
  if (!helperCoord) return routePoints

  const insertPlan = getRoutePointInsertOrderPlan(routePoints, 1, trackClientId)
  if (!insertPlan) return routePoints

  const routePointsWithShiftedOrders = routePoints.map((routePoint) => ({ ...routePoint }))
  for (const { routePoint, newOrder } of insertPlan.shifts) {
    const shiftedRoutePoint = routePointsWithShiftedOrders.find(
      (candidate) => candidate.clientId === routePoint.clientId,
    )
    if (shiftedRoutePoint) {
      shiftedRoutePoint.order = newOrder
    }
  }

  const [helperLon, helperLat] = helperCoord
  routePointsWithShiftedOrders.splice(1, 0, {
    clientId: generateId(),
    lat: helperLat,
    lon: helperLon,
    order: insertPlan.insertOrder,
    trackClientId,
  })

  return routePointsWithShiftedOrders
}

const runAdapterSyncBatch = async <T>(
  adapter: TrackRoutingAdapter,
  operation: () => Promise<T>,
): Promise<T> => {
  if (adapter.runInSyncBatch) {
    return adapter.runInSyncBatch(operation)
  }

  return operation()
}

const materializeInsertedGeometryOnlySegments = (
  lineString: string | undefined,
  lat: number,
  lon: number,
): { insertedLat: number; insertedLon: number; segments: Segment[] } | null => {
  const projection = getTrackLineInsertionProjection(lineString, lat, lon)
  if (!projection) return null

  const { beforeVertices, afterVertices } = projection
  if (beforeVertices.length === 0 || afterVertices.length === 0) return null

  // Place the new point exactly where it was dropped on the map, and connect the
  // split with straight legs to that drop, so the point stays *on* the track
  // rather than snapping back onto the line. The projection only decides where
  // along the line to split; the drop is where the point actually lands.
  const drop: [number, number] = [lon, lat]
  const beforeCoords = appendDropCoord(beforeVertices, drop)
  const afterCoords = prependDropCoord(afterVertices, drop)

  return {
    insertedLat: lat,
    insertedLon: lon,
    segments: [buildSegmentFromCoords(beforeCoords), buildSegmentFromCoords(afterCoords)],
  }
}

/**
 * Geometry-preserving fallback for a multi-route-point drag insert. When BRouter
 * cannot reroute the two split legs (prev→drop and drop→next) — e.g. the dropped
 * point snapped to a disconnected part of the routable network, or routing is
 * temporarily unavailable — the drag must still add a route point instead of
 * hard-failing with "route point not inserted". This splits the single grabbed
 * segment (`segments[insertBeforeIndex - 1]`) at the point where the drop projects
 * onto that segment's own polyline, but places the new point at the *actual drop*
 * and connects the split with straight legs to it — so the point lands where it
 * was dropped on the map (not snapped back onto the line) while the rest of the
 * track geometry is preserved untouched. Shaped to match `recalcSegmentsForInsert`'s
 * `{ deletedIndices, newSegments }` result so callers can substitute it
 * transparently. Returns null only when the segment cannot be split (missing, or
 * fewer than two points).
 */
const buildInsertGeometryFallbackSegments = (
  segments: Segment[],
  insertBeforeIndex: number,
  lat: number,
  lon: number,
): {
  insertedLat: number
  insertedLon: number
  deletedIndices: number[]
  newSegments: { segment: Segment; order: number }[]
} | null => {
  const deletedSegmentIndex = insertBeforeIndex - 1
  const splitSegment = segments[deletedSegmentIndex]
  if (!splitSegment || splitSegment.points.length < 2) return null

  const projection = getTrackLineInsertionProjection(
    buildLineStringFromSegments([splitSegment]),
    lat,
    lon,
  )
  if (!projection) return null

  const { beforeVertices, afterVertices } = projection
  if (beforeVertices.length === 0 || afterVertices.length === 0) return null

  const drop: [number, number] = [lon, lat]
  const beforeCoords = appendDropCoord(beforeVertices, drop)
  const afterCoords = prependDropCoord(afterVertices, drop)

  return {
    insertedLat: lat,
    insertedLon: lon,
    deletedIndices: [deletedSegmentIndex],
    newSegments: [
      { segment: buildSegmentFromCoords(beforeCoords), order: deletedSegmentIndex },
      { segment: buildSegmentFromCoords(afterCoords), order: deletedSegmentIndex + 1 },
    ],
  }
}

const snapInsertTarget = async (
  lat: number,
  lon: number,
): Promise<{ lat: number; lon: number }> => {
  const snapped = await routingService.getNearest(lat, lon)
  if (!snapped) return { lat, lon }

  return { lat: snapped[0], lon: snapped[1] }
}

const clampSparseTrackPointCount = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, Math.round(value)))

const toSparseTrackPreviewPoints = (
  lineString: string,
  pointCount: number,
  minPointCount: number,
  maxPointCount: number,
): SparseTrackPreviewPoint[] =>
  (
    buildSparseTrackHelperCoords(
      lineString,
      clampSparseTrackPointCount(pointCount, minPointCount, maxPointCount),
      maxPointCount,
    ) ?? []
  ).map(([lon, lat]) => ({
    lat,
    lon,
  }))

const buildUndoRangeState = (
  originalSegments: Segment[],
  replacement: { deletedIndices: number[]; newSegments: { segment: Segment; order: number }[] },
  lineString?: string,
): RangeSegmentState => ({
  kind: 'range',
  deletedIndices: [...replacement.newSegments.map(({ order }) => order)].sort((a, b) => a - b),
  newSegments: replacement.deletedIndices.map((index) => ({
    segment: cloneSegment(originalSegments[index]),
    order: index,
  })),
  lineString,
})

function rebuildDrawingTrackPointsFromSegments(segments: Segment[]): [number, number][] {
  const points: [number, number][] = []
  segments.forEach((segment) => {
    segment.points.forEach((point) => {
      points.push([point.lon, point.lat])
    })
  })
  return points
}

export function useTrackRoutingSession(options: UseTrackRoutingSessionOptions) {
  const {
    mapInstance,
    drawingTrackPoints,
    drawingTrackSegments,
    lastClickedPoint,
    isCalculatingRoute,
    adapter: persistAdapter,
  } = options

  const shouldSuppressInsertDragClick = options.shouldSuppressInsertDragClick ?? (() => false)
  const uiStore = useUiStore()
  const routeDraft = ref<RouteDraftState | null>(null)

  const updateRouteDraftDirty = (draft: RouteDraftState) => {
    draft.dirty = !sameRouteDraftValue(
      getRouteDraftFingerprint(draft.baselineTour, draft.trackClientId),
      getRouteDraftFingerprint(draft.draftTour, draft.trackClientId),
    )
  }

  const ensureRouteDraft = (tourId: string, mode?: RouteDraftMode): RouteDraftState | null => {
    if (routeDraft.value?.tourId === tourId) {
      return routeDraft.value
    }

    const sourceTour = persistAdapter.getTour(tourId)
    if (!sourceTour) return null

    const draftMode: RouteDraftMode = mode ?? (uiStore.isTrackEditMode ? 'edit' : 'drawing')
    const trackClientId = draftMode === 'edit' ? uiStore.editingTrackClientId : null
    routeDraft.value = {
      mode: draftMode,
      tourId,
      trackClientId,
      baselineTour: cloneTour(sourceTour),
      draftTour: sourceTour,
      dirty: false,
    }
    updateRouteDraftDirty(routeDraft.value)
    return routeDraft.value
  }

  const markRouteDraftMutated = (draft: RouteDraftState) => {
    updateRouteDraftDirty(draft)
  }

  const clearRouteDraft = () => {
    routeDraft.value = null
  }

  const syncDraftTrackRoutingProfile = (routingProfile: string) => {
    const draft = routeDraft.value
    const trackClientId = draft?.trackClientId
    if (!draft || !trackClientId) return

    const draftTrack = draft.draftTour.tracks.find((track) => track.clientId === trackClientId)
    if (!draftTrack || draftTrack.routingProfile === routingProfile) return

    draftTrack.routingProfile = routingProfile
    markRouteDraftMutated(draft)
  }

  const refreshDraftMetrics = (draft: RouteDraftState, trackClientId?: string | null) => {
    if (trackClientId) {
      const track = draft.draftTour.tracks.find((candidate) => candidate.clientId === trackClientId)
      if (track) {
        refreshTrackMetrics(track)
      }
    }
    refreshTourTrackStats(draft.draftTour)
  }

  const restoreRouteDraftBaseline = () => {
    const draft = routeDraft.value
    if (!draft) return

    const sourceTour = persistAdapter.getTour(draft.tourId)
    if (!sourceTour) return

    const baseline = cloneTour(draft.baselineTour)
    Object.assign(sourceTour, baseline)
    draft.draftTour = sourceTour
    updateRouteDraftDirty(draft)
  }

  const getActiveRouteDraftTour = computed(() => routeDraft.value?.draftTour ?? null)
  const isRouteDraftDirty = computed(() => routeDraft.value?.dirty ?? false)

  const adapter: TrackRoutingAdapter = {
    resolveTargetTourId: () => routeDraft.value?.tourId ?? persistAdapter.resolveTargetTourId(),
    getTour: (tourId) =>
      routeDraft.value?.tourId === tourId
        ? routeDraft.value.draftTour
        : (persistAdapter.getTour(tourId) ?? null),
    runInSyncBatch: async (operation) => operation(),
    addTrack: async (tourId, data) => {
      const draft = ensureRouteDraft(tourId, 'drawing')
      if (!draft) return null

      const clientId = data.clientId ?? generateId()
      const track: Track = {
        clientId,
        name: data.name,
        segments: cloneSegments(data.segments),
        visible: true,
        color: data.color ?? TrackColorService.getNewTrackColorForTour(draft.draftTour.tracks),
        routingProfile: data.routingProfile ?? uiStore.routingProfile,
        type: data.type,
        _previousState: undefined,
      }
      draft.draftTour.tracks.push(track)
      draft.trackClientId = clientId
      refreshDraftMetrics(draft, clientId)
      markRouteDraftMutated(draft)
      return clientId
    },
    deleteTrack: async (tourId, trackClientId) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return

      draft.draftTour.tracks = draft.draftTour.tracks.filter(
        (track) => track.clientId !== trackClientId,
      )
      draft.draftTour.routePoints = draft.draftTour.routePoints.filter(
        (routePoint) => (routePoint.trackClientId ?? null) !== trackClientId,
      )
      refreshDraftMetrics(draft)
      markRouteDraftMutated(draft)
    },
    addRoutePoint: async (tourId, data) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return

      const trackClientId = requireTrackClientId(data.trackClientId, 'Draft route point')
      const order =
        data.order ?? getSparseTrackAppendOrder(draft.draftTour.routePoints, trackClientId)

      draft.draftTour.routePoints.push({
        clientId: data.clientId ?? generateId(),
        lat: data.lat,
        lon: data.lon,
        ele: data.ele,
        name: data.name,
        description: data.description,
        order,
        trackClientId,
      })
      markRouteDraftMutated(draft)
    },
    insertRoutePointAt: async (tourId, insertIndex, data) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return
      if (insertIndex < 0 || insertIndex > draft.draftTour.routePoints.length) return

      const trackClientId = requireTrackClientId(
        data.trackClientId ??
          (insertIndex > 0
            ? draft.draftTour.routePoints[insertIndex - 1].trackClientId
            : undefined) ??
          (insertIndex < draft.draftTour.routePoints.length
            ? draft.draftTour.routePoints[insertIndex].trackClientId
            : undefined),
        'Inserted draft route point',
      )
      const insertPlan = getRoutePointInsertOrderPlan(
        draft.draftTour.routePoints,
        insertIndex,
        trackClientId,
      )
      if (!insertPlan) return

      draft.draftTour.routePoints.splice(insertIndex, 0, {
        clientId: data.clientId ?? generateId(),
        lat: data.lat,
        lon: data.lon,
        ele: data.ele,
        name: data.name,
        description: data.description,
        order: insertPlan.insertOrder,
        trackClientId,
      })
      for (const { routePoint, newOrder } of insertPlan.shifts) {
        routePoint.order = newOrder
      }
      markRouteDraftMutated(draft)
    },
    insertRoutePointsAt: async (tourId, insertIndex, data) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return
      if (insertIndex < 0 || insertIndex > draft.draftTour.routePoints.length) return
      if (data.length === 0) return

      const trackClientId = requireTrackClientId(
        data[0]?.trackClientId ??
          (insertIndex > 0
            ? draft.draftTour.routePoints[insertIndex - 1].trackClientId
            : undefined) ??
          (insertIndex < draft.draftTour.routePoints.length
            ? draft.draftTour.routePoints[insertIndex].trackClientId
            : undefined),
        'Inserted draft route points',
      )
      const insertPlan = getRoutePointMultiInsertOrderPlan(
        draft.draftTour.routePoints,
        insertIndex,
        data.length,
        trackClientId,
      )
      if (!insertPlan) return

      draft.draftTour.routePoints.splice(
        insertIndex,
        0,
        ...data.map((routePoint, index) => ({
          clientId: routePoint.clientId ?? generateId(),
          lat: routePoint.lat,
          lon: routePoint.lon,
          ele: routePoint.ele,
          name: routePoint.name,
          description: routePoint.description,
          order: insertPlan.insertOrders[index] ?? insertPlan.insertOrderStart + index,
          trackClientId,
        })),
      )
      for (const { routePoint, newOrder } of insertPlan.shifts) {
        routePoint.order = newOrder
      }
      markRouteDraftMutated(draft)
    },
    deleteRoutePoint: async (tourId, routePointIndex) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return
      if (routePointIndex < 0 || routePointIndex >= draft.draftTour.routePoints.length) return

      draft.draftTour.routePoints.splice(routePointIndex, 1)
      markRouteDraftMutated(draft)
    },
    updateRoutePointPosition: async (tourId, routePointIndex, newLat, newLon) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return
      const routePoint = draft.draftTour.routePoints[routePointIndex]
      if (!routePoint) return
      routePoint.lat = newLat
      routePoint.lon = newLon
      markRouteDraftMutated(draft)
    },
    reorderTrackRoutePoints: async (tourId, trackClientId, orderedRoutePointClientIds) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return

      const originalRoutePoints = [...draft.draftTour.routePoints]
      const trackRoutePoints = originalRoutePoints.filter(
        (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
      )
      if (trackRoutePoints.length !== orderedRoutePointClientIds.length) return

      const reorderPlan = getSparseTrackReorderPlan(
        draft.draftTour.routePoints,
        trackClientId,
        orderedRoutePointClientIds,
      )
      const reorderedTrackRoutePoints = reorderPlan.reorderedRoutePoints
      reorderPlan.changedRoutePoints.forEach(({ routePoint, newOrder }) => {
        routePoint.order = newOrder
      })
      let nextTrackRoutePointIndex = 0
      draft.draftTour.routePoints = originalRoutePoints.map((routePoint) =>
        (routePoint.trackClientId ?? null) === trackClientId
          ? reorderedTrackRoutePoints[nextTrackRoutePointIndex++]
          : routePoint,
      )
      markRouteDraftMutated(draft)
    },
    replaceSegments: async (tourId, trackClientId, deletedIndices, newSegments, optionsArg) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return
      const track = draft.draftTour.tracks.find((candidate) => candidate.clientId === trackClientId)
      if (!track) return

      const validDeletedIndices = deletedIndices.filter(
        (index) => index >= 0 && index < track.segments.length,
      )
      const sortedDeleted = [...validDeletedIndices].sort((a, b) => a - b)
      const sortedNew = [...newSegments].sort((a, b) => a.order - b.order)
      const isInPlace =
        sortedDeleted.length > 0 &&
        sortedDeleted.length === sortedNew.length &&
        sortedDeleted.every((index, i) => index === sortedNew[i].order)

      if (isInPlace) {
        for (let i = 0; i < sortedDeleted.length; i += 1) {
          const oldSegment = track.segments[sortedDeleted[i]]
          const newSegment = cloneSegment(sortedNew[i].segment)
          newSegment.clientId = oldSegment.clientId
          track.segments[sortedDeleted[i]] = newSegment
        }
      } else {
        for (const index of [...sortedDeleted].sort((a, b) => b - a)) {
          track.segments.splice(index, 1)
        }
        for (const { segment, order } of sortedNew) {
          track.segments.splice(Math.min(order, track.segments.length), 0, cloneSegment(segment))
        }
      }

      if (!optionsArg?.skipLineStringRebuild) {
        track.pointsLineReduced5m = buildLineStringFromSegments(track.segments)
      }
      refreshDraftMetrics(draft, trackClientId)
      markRouteDraftMutated(draft)
    },
    replaceAllSegments: async (tourId, trackClientId, _oldSegmentClientIds, newSegments) => {
      const draft = ensureRouteDraft(tourId)
      if (!draft) return
      const track = draft.draftTour.tracks.find((candidate) => candidate.clientId === trackClientId)
      if (!track) return

      track.segments = cloneSegments(newSegments)
      track.pointsLineReduced5m = buildLineStringFromSegments(track.segments)
      refreshDraftMetrics(draft, trackClientId)
      markRouteDraftMutated(draft)
    },
    onLineStringUpdated: (tourId, trackClientId, lineString) => {
      const draft = routeDraft.value?.tourId === tourId ? routeDraft.value : null
      const track = draft?.draftTour.tracks.find(
        (candidate) => candidate.clientId === trackClientId,
      )
      if (track) {
        track.pointsLineReduced5m = lineString
        markRouteDraftMutated(draft!)
      }
    },
    onDrawingDiscard: persistAdapter.onDrawingDiscard,
    onExit: persistAdapter.onExit,
  }

  const {
    isDragging: isTrackDragging,
    cancelDrag: cancelTrackDragging,
    shouldSuppressClick: shouldSuppressDragClick,
  } = useTrackDrag({
    mapInstance,
    drawingTrackSegments,
    drawingTrackPoints,
    isCalculatingRoute,
    onInsert: async (trackInsertIndex, lat, lon) => {
      await handleDrawingTrackInsertDrop(trackInsertIndex, lat, lon)
    },
  })

  const drawingTrackClientId = ref<string | null>(null)
  const drawingUndoStack = ref<DrawingHistoryEntry[]>([])
  const drawingRedoStack = ref<DrawingHistoryEntry[]>([])
  const isApplyingDrawingHistory = ref(false)

  const canUndoTrackDrawing = computed(() => drawingUndoStack.value.length > 0)
  const canRedoTrackDrawing = computed(() => drawingRedoStack.value.length > 0)

  const drawingPendingRoutePoints = computed(() => {
    const points: { lat: number; lon: number }[] = []

    if (!drawingTrackClientId.value) {
      for (const segment of drawingTrackSegments.value) {
        if (segment.points.length === 0) continue
        const lastPoint = segment.points[segment.points.length - 1]
        points.push({ lat: lastPoint.lat, lon: lastPoint.lon })
      }
    }

    if (uiStore.isTrackDrawingActive && isCalculatingRoute.value && spinningRoutePoint.value) {
      const alreadyIncluded = points.some(
        (point) =>
          point.lat === spinningRoutePoint.value?.lat &&
          point.lon === spinningRoutePoint.value?.lon,
      )
      if (!alreadyIncluded) {
        points.push({ ...spinningRoutePoint.value })
      }
    }

    return points
  })

  const setSpinnerPoint = (lat: number, lon: number) => {
    setSpinningRoutePoints([{ lat, lon }])
  }

  const setNeighborSpinnerPoints = (
    routePoints: Array<Pick<RoutePoint, 'lat' | 'lon'>>,
    localIndex: number,
    current: Pick<RoutePoint, 'lat' | 'lon'> | null = null,
  ) => {
    setSpinningRoutePoints([
      routePoints[localIndex - 1],
      current ?? routePoints[localIndex],
      routePoints[localIndex + 1],
    ])
  }

  const reorderTrackRoutePoints = async (
    tourId: string,
    trackClientId: string,
    orderedRoutePointClientIds: string[],
  ) => {
    if (!adapter.reorderTrackRoutePoints) {
      throw new Error('Track route-point reordering is not available for this adapter')
    }
    await adapter.reorderTrackRoutePoints(tourId, trackClientId, orderedRoutePointClientIds)
  }

  const applyDrawingLocalState = (state: DrawingLocalState) => {
    drawingTrackClientId.value = state.trackClientId
    drawingTrackSegments.value = cloneSegments(state.segments)
    drawingTrackPoints.value = rebuildDrawingTrackPointsFromSegments(drawingTrackSegments.value)
    lastClickedPoint.value = state.lastClickedPoint ? [...state.lastClickedPoint] : null
  }

  const buildDrawingLocalStateFromTrack = (
    trackClientId: string,
    routePoints: { lat: number; lon: number }[],
    trackSegments: Segment[],
  ): DrawingLocalState => {
    const seed =
      routePoints.length > 0
        ? [
            {
              clientId: generateId(),
              points: [
                {
                  clientId: generateId(),
                  lat: routePoints[0].lat,
                  lon: routePoints[0].lon,
                  time: Date.now(),
                },
              ],
            },
          ]
        : []
    const lastAnchor = routePoints[routePoints.length - 1]

    return {
      trackClientId,
      segments: [...seed, ...cloneSegments(trackSegments)],
      lastClickedPoint: lastAnchor ? [lastAnchor.lat, lastAnchor.lon] : null,
    }
  }

  const snapshotDrawingLocalState = (): DrawingLocalState => ({
    trackClientId: drawingTrackClientId.value,
    segments: cloneSegments(drawingTrackSegments.value),
    lastClickedPoint: lastClickedPoint.value ? [...lastClickedPoint.value] : null,
  })

  const clearDrawingLocalState = () => {
    drawingTrackClientId.value = null
    drawingTrackPoints.value = []
    drawingTrackSegments.value = []
    lastClickedPoint.value = null
  }

  const resetSharedDrawingState = () => {
    clearDrawingLocalState()
    drawingUndoStack.value = []
    drawingRedoStack.value = []
    isCalculatingRoute.value = false
    clearSpinningRoutePoints()
  }

  const pushDrawingHistoryEntry = (entry: DrawingHistoryEntry) => {
    if (isApplyingDrawingHistory.value) return
    drawingUndoStack.value.push(entry)
    drawingRedoStack.value = []
  }

  const executeDrawingHistory = async (direction: 'undo' | 'redo') => {
    const sourceStack = direction === 'undo' ? drawingUndoStack.value : drawingRedoStack.value
    const targetStack = direction === 'undo' ? drawingRedoStack.value : drawingUndoStack.value
    const entry = sourceStack.pop()
    if (!entry) return

    try {
      isApplyingDrawingHistory.value = true
      isCalculatingRoute.value = true
      clearSpinningRoutePoints()
      await entry[direction]()
      targetStack.push(entry)
    } catch (error) {
      sourceStack.push(entry)
      logService.error(LOG_CATEGORIES.MAP, `Failed to ${direction} track drawing`, { error })
      showError(`Failed to ${direction} track drawing`)
    } finally {
      isApplyingDrawingHistory.value = false
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const beginDrawing = () => {
    clearRouteDraft()
    uiStore.setIsTrackDrawingActive(true)
    resetSharedDrawingState()
  }

  const confirmDiscardDraft = async (mode: RouteDraftMode): Promise<boolean> => {
    const confirmed = await showConfirm({
      title: 'Discard route changes?',
      message:
        mode === 'drawing'
          ? 'Discard this unsaved track drawing?'
          : 'Discard the unsaved route edits?',
      confirmText: 'Discard',
      cancelText: 'Keep Editing',
      variant: 'warning',
    })
    return confirmed
  }

  const hasUnsavedDrawingChanges = computed(
    () => isRouteDraftDirty.value || drawingTrackSegments.value.length > 0,
  )
  const canSaveTrackDrawing = computed(
    () =>
      !!drawingTrackClientId.value &&
      !!routeDraft.value?.dirty &&
      !!routeDraft.value.draftTour.tracks.find(
        (track) => track.clientId === drawingTrackClientId.value,
      ),
  )
  const canSaveTrackEdit = computed(() => !!routeDraft.value?.dirty)

  const exitTrackDrawing = (options: { restoreDraft: boolean }) => {
    abortPendingRouting()
    if (options.restoreDraft) {
      restoreRouteDraftBaseline()
    }
    uiStore.setIsTrackDrawingActive(false)
    adapter.onExit?.()
    clearRouteDraft()
    resetSharedDrawingState()
  }

  const discardTrackDrawing = () => {
    adapter.onDrawingDiscard?.()
    exitTrackDrawing({ restoreDraft: true })
  }

  const cancelTrackDrawing = async () => {
    if (hasUnsavedDrawingChanges.value && !(await confirmDiscardDraft('drawing'))) {
      return
    }
    discardTrackDrawing()
  }

  const saveTrackDrawing = async () => {
    const draft = routeDraft.value
    const trackClientId = drawingTrackClientId.value
    if (!draft || !draft.dirty || !trackClientId) {
      exitTrackDrawing({ restoreDraft: false })
      return
    }

    const draftTrack = draft.draftTour.tracks.find((track) => track.clientId === trackClientId)
    if (!draftTrack) {
      exitTrackDrawing({ restoreDraft: false })
      return
    }

    if (!persistAdapter.commitTrackRouteDraft) {
      throw new Error('Route draft saving is not available for this adapter')
    }

    const draftTrackSnapshot = cloneTrack(draftTrack)
    draftTrackSnapshot.routingProfile = uiStore.routingProfile
    const draftRoutePoints = addMinimumSavedSparseTrackAnchors(
      draftTrackSnapshot,
      trackClientId,
      cloneRoutePoints(getTrackRoutePoints(draft.draftTour.routePoints, trackClientId)),
    )

    await persistAdapter.commitTrackRouteDraft({
      mode: 'drawing',
      tourId: draft.tourId,
      trackClientId,
      draftTrack: draftTrackSnapshot,
      draftRoutePoints,
      baselineTrack: null,
      baselineRoutePoints: [],
    })
    exitTrackDrawing({ restoreDraft: false })
  }

  const findRoutePointIndexByClientId = (
    tourId: string,
    routePointClientId: string,
  ): number | null => {
    const tour = adapter.getTour(tourId)
    if (!tour) return null
    const routePointIndex = tour.routePoints.findIndex(
      (routePoint) => routePoint.clientId === routePointClientId,
    )
    return routePointIndex >= 0 ? routePointIndex : null
  }

  const persistCreatedTrack = async (
    tourId: string,
    trackClientId: string,
    trackName: string,
    segmentSnapshot: Segment,
    routePoints: [
      Pick<RoutePoint, 'clientId' | 'lat' | 'lon'>,
      Pick<RoutePoint, 'clientId' | 'lat' | 'lon'>,
    ],
  ) => {
    let createdTrackClientId: string | null = null
    await runAdapterSyncBatch(adapter, async () => {
      createdTrackClientId = await adapter.addTrack(tourId, {
        clientId: trackClientId,
        name: trackName,
        segments: [cloneSegment(segmentSnapshot)],
      })
      if (!createdTrackClientId) {
        throw new Error('Failed to create track')
      }

      const [firstOrder, secondOrder] = getSparseTrackSeedOrders(2)

      await adapter.addRoutePoint(tourId, {
        ...routePoints[0],
        order: firstOrder,
        trackClientId,
      })
      await adapter.addRoutePoint(tourId, {
        ...routePoints[1],
        order: secondOrder,
        trackClientId,
      })
    })
    if (!createdTrackClientId) {
      throw new Error('Failed to create track')
    }
  }

  const deletePersistedTrack = async (tourId: string, trackClientId: string) => {
    await adapter.deleteTrack(tourId, trackClientId)
  }

  const deleteRoutePointByClientIdFromTrack = async (
    tourId: string,
    routePointClientId: string,
  ) => {
    const routePointIndex = findRoutePointIndexByClientId(tourId, routePointClientId)
    if (routePointIndex === null) return
    const tour = adapter.getTour(tourId)
    if (!tour) return
    await adapter.deleteRoutePoint(tourId, routePointIndex)
  }

  const persistAppendedSegment = async (
    tourId: string,
    trackClientId: string,
    segmentSnapshot: Segment,
    routePointSnapshot: Pick<RoutePoint, 'clientId' | 'lat' | 'lon'>,
  ) => {
    const tour = adapter.getTour(tourId)
    const track = tour?.tracks.find((candidate) => candidate.clientId === trackClientId)
    if (!tour || !track) {
      throw new Error('Track not available for drawing session')
    }

    const segmentOrder = track.segments.length
    const routePointOrder = getSparseTrackAppendOrder(tour.routePoints, trackClientId)

    await adapter.replaceSegments(
      tourId,
      trackClientId,
      [],
      [{ segment: cloneSegment(segmentSnapshot), order: segmentOrder }],
    )
    await adapter.addRoutePoint(tourId, {
      ...routePointSnapshot,
      order: routePointOrder,
      trackClientId,
    })
  }

  const removeAppendedSegment = async (
    tourId: string,
    trackClientId: string,
    segmentOrder: number,
    routePointClientId: string,
  ) => {
    await adapter.replaceSegments(tourId, trackClientId, [segmentOrder], [])
    await deleteRoutePointByClientIdFromTrack(tourId, routePointClientId)
  }

  const handleSeedPoint = (targetPoint: [number, number]) => {
    const beforeState = snapshotDrawingLocalState()
    const seedPoint: Waypoint = {
      clientId: generateId(),
      lat: targetPoint[0],
      lon: targetPoint[1],
      time: Date.now(),
    }
    const seedSegment: Segment = { clientId: generateId(), points: [seedPoint] }
    const afterState: DrawingLocalState = {
      trackClientId: null,
      segments: [...beforeState.segments, seedSegment],
      lastClickedPoint: targetPoint,
    }

    applyDrawingLocalState(afterState)
    pushDrawingHistoryEntry({
      undo: async () => applyDrawingLocalState(beforeState),
      redo: async () => applyDrawingLocalState(afterState),
    })
  }

  const handleCreatePersistedTrack = async (
    tourId: string,
    targetPoint: [number, number],
    segmentSnapshot: Segment,
  ) => {
    const beforeState = snapshotDrawingLocalState()
    const firstAnchor = beforeState.segments[0]?.points[0]
    if (!firstAnchor) {
      throw new Error('Missing initial drawing anchor')
    }

    const trackClientId = generateId()
    const routePointSnapshots: [
      Pick<RoutePoint, 'clientId' | 'lat' | 'lon'>,
      Pick<RoutePoint, 'clientId' | 'lat' | 'lon'>,
    ] = [
      {
        clientId: generateId(),
        lat: firstAnchor.lat,
        lon: firstAnchor.lon,
      },
      {
        clientId: generateId(),
        lat: targetPoint[0],
        lon: targetPoint[1],
      },
    ]
    const trackName = `New Track ${new Date().toLocaleTimeString()}`
    const afterState: DrawingLocalState = {
      trackClientId,
      segments: [...beforeState.segments, cloneSegment(segmentSnapshot)],
      lastClickedPoint: targetPoint,
    }

    await persistCreatedTrack(
      tourId,
      trackClientId,
      trackName,
      segmentSnapshot,
      routePointSnapshots,
    )
    applyDrawingLocalState(afterState)

    pushDrawingHistoryEntry({
      undo: async () => {
        await deletePersistedTrack(tourId, trackClientId)
        applyDrawingLocalState(beforeState)
      },
      redo: async () => {
        await persistCreatedTrack(
          tourId,
          trackClientId,
          trackName,
          segmentSnapshot,
          routePointSnapshots,
        )
        applyDrawingLocalState(afterState)
      },
    })
  }

  const handleAppendPersistedSegment = async (
    tourId: string,
    trackClientId: string,
    targetPoint: [number, number],
    segmentSnapshot: Segment,
  ) => {
    const beforeState = snapshotDrawingLocalState()
    const afterState: DrawingLocalState = {
      trackClientId,
      segments: [...beforeState.segments, cloneSegment(segmentSnapshot)],
      lastClickedPoint: targetPoint,
    }
    const routePointSnapshot: Pick<RoutePoint, 'clientId' | 'lat' | 'lon'> = {
      clientId: generateId(),
      lat: targetPoint[0],
      lon: targetPoint[1],
    }
    const segmentOrder = beforeState.segments.length - 1

    await persistAppendedSegment(tourId, trackClientId, segmentSnapshot, routePointSnapshot)
    applyDrawingLocalState(afterState)

    pushDrawingHistoryEntry({
      undo: async () => {
        await removeAppendedSegment(
          tourId,
          trackClientId,
          segmentOrder,
          routePointSnapshot.clientId,
        )
        applyDrawingLocalState(beforeState)
      },
      redo: async () => {
        await persistAppendedSegment(tourId, trackClientId, segmentSnapshot, routePointSnapshot)
        applyDrawingLocalState(afterState)
      },
    })
  }

  const addDrawingRoutePoint = async (lat: number, lon: number): Promise<boolean> => {
    if (!uiStore.isTrackDrawingActive || isCalculatingRoute.value) return false

    try {
      isCalculatingRoute.value = true
      setSpinnerPoint(lat, lon)

      const snapped = await routingService.getNearest(lat, lon)
      const targetPoint = snapped || [lat, lon]

      if (!lastClickedPoint.value) {
        handleSeedPoint(targetPoint)
        return true
      }

      const route = await routingService.getRoute(
        [lastClickedPoint.value, targetPoint],
        uiStore.routingProfile,
      )
      if (!route) {
        if (!uiStore.isTrackDrawingActive) return false
        showError('Routing failed – please try a different point')
        return true
      }

      const segmentSnapshot = routeResultToSegmentWithTime(route, Date.now())
      if (segmentSnapshot.points.length === 0) return true

      const tourId = adapter.resolveTargetTourId()
      if (!tourId) {
        showError('No tour available to add track')
        return true
      }

      if (!drawingTrackClientId.value) {
        await handleCreatePersistedTrack(tourId, targetPoint, segmentSnapshot)
        return true
      }

      await handleAppendPersistedSegment(
        tourId,
        drawingTrackClientId.value,
        targetPoint,
        segmentSnapshot,
      )
      return true
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error adding track point', { error })
      showError('Failed to add track point')
      return true
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const handleDrawingClick = async (event: MapMouseEvent) => {
    if (shouldSuppressDragClick()) return
    await addDrawingRoutePoint(event.lngLat.lat, event.lngLat.lng)
  }

  const editModeNewSegments = ref<Segment[]>([])
  const editUndoStack = ref<EditHistoryEntry[]>([])
  const editRedoStack = ref<EditHistoryEntry[]>([])
  const isApplyingEditHistory = ref(false)
  const sparseTrackSetup = ref<SparseTrackSetupState | null>(null)
  const sparseTrackApplyState = ref<SparseTrackApplyState | null>(null)

  const justEnteredEditMode = ref(false)
  const markJustEnteredEditMode = () => {
    justEnteredEditMode.value = true
    queueMicrotask(() => {
      justEnteredEditMode.value = false
    })
  }

  const canUndoTrackEdit = computed(() => editUndoStack.value.length > 0)
  const canRedoTrackEdit = computed(() => editRedoStack.value.length > 0)
  const editModeHasRoutePointChanges = computed(
    () => canUndoTrackEdit.value || canRedoTrackEdit.value,
  )
  const isSparseTrackSetupOpen = computed(() => sparseTrackSetup.value !== null)
  const isApplyingSparseTrackSetup = computed(() => sparseTrackApplyState.value !== null)
  const sparseTrackPreviewRoutePoints = computed(
    () =>
      sparseTrackSetup.value?.previewRoutePoints ??
      sparseTrackApplyState.value?.remainingPreviewRoutePoints ??
      [],
  )

  const drawingPendingRoutePreviewPoints = computed(() => {
    if (uiStore.isTrackDrawingActive && drawingTrackSegments.value.length > 0) {
      const points: { lat: number; lon: number }[] = []
      for (const seg of drawingTrackSegments.value) {
        if (seg.points.length === 0) continue
        const lastPoint = seg.points[seg.points.length - 1]
        points.push({ lat: lastPoint.lat, lon: lastPoint.lon })
      }
      return points
    }
    return []
  })

  const resetEditState = () => {
    sparseTrackSetup.value = null
    sparseTrackApplyState.value = null
    editModeNewSegments.value = []
    editUndoStack.value = []
    editRedoStack.value = []
    drawingTrackPoints.value = []
    drawingTrackSegments.value = []
    lastClickedPoint.value = null
    isCalculatingRoute.value = false
    clearSpinningRoutePoints()
  }

  const initEditModeLastClickedPoint = (track: Tour['tracks'][0]) => {
    const direction = uiStore.editingTrackAppendDirection
    if (direction === 'end') {
      const lastSeg = track.segments?.[track.segments.length - 1]
      if (lastSeg?.points?.length) {
        const point = lastSeg.points[lastSeg.points.length - 1]
        lastClickedPoint.value = [point.lat, point.lon]
        return
      }
      if (track.pointsLineReduced5m) {
        const coords = parseLineString(track.pointsLineReduced5m)
        if (coords.length > 0) {
          const last = coords[coords.length - 1]
          lastClickedPoint.value = [last[1], last[0]]
          return
        }
      }
    } else {
      const firstSeg = track.segments?.[0]
      if (firstSeg?.points?.length) {
        const point = firstSeg.points[0]
        lastClickedPoint.value = [point.lat, point.lon]
        return
      }
      if (track.pointsLineReduced5m) {
        const coords = parseLineString(track.pointsLineReduced5m)
        if (coords.length > 0) {
          const first = coords[0]
          lastClickedPoint.value = [first[1], first[0]]
          return
        }
      }
    }

    lastClickedPoint.value = null
  }

  const getEditingTrackContext = () => {
    const tourId = uiStore.editingTrackTourId
    const trackClientId = uiStore.editingTrackClientId
    if (!tourId || !trackClientId) return null

    const tour = adapter.getTour(tourId)
    if (!tour) return null

    const track = tour.tracks.find((candidate) => candidate.clientId === trackClientId)
    if (!track) return null

    return { tourId, trackClientId, tour, track }
  }

  const getDrawingTrackContext = () => {
    if (!uiStore.isTrackDrawingActive || !drawingTrackClientId.value) return null

    const tourId = adapter.resolveTargetTourId()
    if (!tourId) return null

    const tour = adapter.getTour(tourId)
    if (!tour) return null

    const track = tour.tracks.find((candidate) => candidate.clientId === drawingTrackClientId.value)
    if (!track) return null

    return { tourId, trackClientId: drawingTrackClientId.value, tour, track }
  }

  const canReverseTrackDrawing = computed(() => {
    const context = getDrawingTrackContext()
    if (!context) return false
    return getTrackRoutePoints(context.tour.routePoints, context.trackClientId).length >= 2
  })

  const canReverseTrackEdit = computed(() => {
    const context = getEditingTrackContext()
    if (!context) return false
    return getTrackRoutePoints(context.tour.routePoints, context.trackClientId).length >= 2
  })

  const syncTrackLineString = (
    tourId: string,
    trackClientId: string,
    lineString: string | undefined,
  ) => {
    const tour = adapter.getTour(tourId)
    const track = tour?.tracks.find((candidate) => candidate.clientId === trackClientId)
    if (!track) return

    track.pointsLineReduced5m = lineString
    if (lineString) {
      adapter.onLineStringUpdated?.(tourId, trackClientId, lineString)
    }
  }

  const applySegmentState = async (
    tourId: string,
    trackClientId: string,
    segmentState: SegmentState,
  ) => {
    const currentTour = adapter.getTour(tourId)
    const currentTrack = currentTour?.tracks.find(
      (candidate) => candidate.clientId === trackClientId,
    )
    if (!currentTrack) return

    if (segmentState.kind === 'range') {
      await adapter.replaceSegments(
        tourId,
        trackClientId,
        [...segmentState.deletedIndices],
        cloneRangeSegments(segmentState.newSegments),
      )
    } else {
      const oldSegmentClientIds = currentTrack.segments
        .map((segment) => segment.clientId)
        .filter((clientId): clientId is string => !!clientId)
      await adapter.replaceAllSegments(
        tourId,
        trackClientId,
        oldSegmentClientIds,
        cloneSegments(segmentState.segments),
      )
    }

    if (segmentState.lineString !== undefined) {
      syncTrackLineString(tourId, trackClientId, segmentState.lineString)
    }
  }

  const materializeSegments = (baseSegments: Segment[], segmentState: SegmentState): Segment[] => {
    if (segmentState.kind === 'full') {
      return cloneSegments(segmentState.segments)
    }

    const nextSegments = cloneSegments(baseSegments)
    for (const index of [...segmentState.deletedIndices].sort((a, b) => b - a)) {
      nextSegments.splice(index, 1)
    }
    for (const { segment, order } of [...segmentState.newSegments].sort(
      (a, b) => a.order - b.order,
    )) {
      nextSegments.splice(order, 0, cloneSegment(segment))
    }
    return nextSegments
  }

  const findEditingRoutePointContext = (tourId: string, routePointIndex: number) => {
    const editingTrack = getEditingTrackContext()
    if (!editingTrack || editingTrack.tourId !== tourId) return null

    const routePoint = editingTrack.tour.routePoints[routePointIndex]
    if (!routePoint || routePoint.trackClientId !== editingTrack.trackClientId) return null

    const trackLocalIndex = getTrackLocalIndexFromGlobalIndex(
      editingTrack.tour.routePoints,
      editingTrack.trackClientId,
      routePointIndex,
    )
    if (trackLocalIndex === null) return null

    return {
      ...editingTrack,
      routePoint,
      routePointIndex,
      trackLocalIndex,
      trackRoutePoints: getTrackRoutePoints(
        editingTrack.tour.routePoints,
        editingTrack.trackClientId,
      ),
    }
  }

  const findDrawingRoutePointContext = (tourId: string, routePointIndex: number) => {
    const drawingTrack = getDrawingTrackContext()
    if (!drawingTrack || drawingTrack.tourId !== tourId) return null

    const routePoint = drawingTrack.tour.routePoints[routePointIndex]
    if (!routePoint || routePoint.trackClientId !== drawingTrack.trackClientId) return null

    const trackLocalIndex = getTrackLocalIndexFromGlobalIndex(
      drawingTrack.tour.routePoints,
      drawingTrack.trackClientId,
      routePointIndex,
    )
    if (trackLocalIndex === null) return null

    return {
      ...drawingTrack,
      routePoint,
      routePointIndex,
      trackLocalIndex,
      trackRoutePoints: getTrackRoutePoints(
        drawingTrack.tour.routePoints,
        drawingTrack.trackClientId,
      ),
    }
  }

  const pushEditHistoryEntry = (entry: EditHistoryEntry) => {
    if (isApplyingEditHistory.value) return
    editUndoStack.value.push(entry)
    editRedoStack.value = []
  }

  const refreshEditModeAnchor = () => {
    const editingTrack = getEditingTrackContext()
    if (editingTrack) {
      initEditModeLastClickedPoint(editingTrack.track)
    }
  }

  const executeEditHistory = async (direction: 'undo' | 'redo') => {
    const sourceStack = direction === 'undo' ? editUndoStack.value : editRedoStack.value
    const targetStack = direction === 'undo' ? editRedoStack.value : editUndoStack.value
    const entry = sourceStack.pop()
    if (!entry) return

    try {
      isApplyingEditHistory.value = true
      isCalculatingRoute.value = true
      clearSpinningRoutePoints()
      await entry[direction]()
      targetStack.push(entry)
      refreshEditModeAnchor()
    } catch (error) {
      sourceStack.push(entry)
      logService.error(LOG_CATEGORIES.MAP, `Failed to ${direction} route edit`, { error })
      showError(`Failed to ${direction} route edit`)
    } finally {
      isApplyingEditHistory.value = false
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const exitTrackEdit = (options: { restoreDraft: boolean }) => {
    abortPendingRouting()
    if (options.restoreDraft) {
      restoreRouteDraftBaseline()
    }
    adapter.onExit?.()
    uiStore.exitTrackEditMode()
    clearRouteDraft()
    resetEditState()
  }

  const discardTrackEdit = () => {
    exitTrackEdit({ restoreDraft: true })
  }

  const cancelTrackEdit = async () => {
    if (isRouteDraftDirty.value && !(await confirmDiscardDraft('edit'))) {
      return
    }
    discardTrackEdit()
  }

  const saveTrackEdit = async () => {
    const draft = routeDraft.value
    const trackClientId = uiStore.editingTrackClientId ?? draft?.trackClientId ?? null
    if (!draft || !draft.dirty || !trackClientId) {
      exitTrackEdit({ restoreDraft: false })
      return
    }

    const draftTrack = draft.draftTour.tracks.find((track) => track.clientId === trackClientId)
    if (!draftTrack) {
      exitTrackEdit({ restoreDraft: false })
      return
    }

    if (!persistAdapter.commitTrackRouteDraft) {
      throw new Error('Route draft saving is not available for this adapter')
    }

    const draftTrackSnapshot = cloneTrack(draftTrack)
    draftTrackSnapshot.routingProfile = uiStore.routingProfile
    const draftRoutePoints = addMinimumSavedSparseTrackAnchors(
      draftTrackSnapshot,
      trackClientId,
      cloneRoutePoints(getTrackRoutePoints(draft.draftTour.routePoints, trackClientId)),
    )

    await persistAdapter.commitTrackRouteDraft({
      mode: 'edit',
      tourId: draft.tourId,
      trackClientId,
      draftTrack: draftTrackSnapshot,
      draftRoutePoints,
      baselineTrack:
        draft.baselineTour.tracks.find((track) => track.clientId === trackClientId) ?? null,
      baselineRoutePoints: cloneRoutePoints(
        getTrackRoutePoints(draft.baselineTour.routePoints, trackClientId),
      ),
    })
    exitTrackEdit({ restoreDraft: false })
  }

  const closeTrackDrawing = cancelTrackDrawing
  const closeTrackEdit = cancelTrackEdit

  const deleteRoutePointByClientId = async (tourId: string, routePointClientId: string) => {
    const routePointIndex = findRoutePointIndexByClientId(tourId, routePointClientId)
    if (routePointIndex === null) return
    await adapter.deleteRoutePoint(tourId, routePointIndex)
  }

  const insertTrackedRoutePoint = async (
    tourId: string,
    trackClientId: string,
    trackInsertIndex: number,
    routePoint: Pick<RoutePoint, 'clientId' | 'lat' | 'lon' | 'ele' | 'name' | 'description'>,
  ) => {
    const tour = adapter.getTour(tourId)
    if (!tour) return

    const globalInsertIndex = getTrackGlobalInsertIndex(
      tour.routePoints,
      trackClientId,
      trackInsertIndex,
    )
    if (globalInsertIndex === null) return

    await adapter.insertRoutePointAt(tourId, globalInsertIndex, {
      clientId: routePoint.clientId,
      lat: routePoint.lat,
      lon: routePoint.lon,
      ele: routePoint.ele,
      name: routePoint.name,
      description: routePoint.description,
      trackClientId,
    })
  }

  const insertTrackedRoutePoints = async (
    tourId: string,
    trackClientId: string,
    trackInsertIndex: number,
    routePoints: Array<
      Pick<RoutePoint, 'clientId' | 'lat' | 'lon' | 'ele' | 'name' | 'description'>
    >,
  ) => {
    const tour = adapter.getTour(tourId)
    if (!tour) return

    const globalInsertIndex = getTrackGlobalInsertIndex(
      tour.routePoints,
      trackClientId,
      trackInsertIndex,
    )
    if (globalInsertIndex === null) return

    if (adapter.insertRoutePointsAt) {
      await adapter.insertRoutePointsAt(
        tourId,
        globalInsertIndex,
        routePoints.map((routePoint) => ({
          clientId: routePoint.clientId,
          lat: routePoint.lat,
          lon: routePoint.lon,
          ele: routePoint.ele,
          name: routePoint.name,
          description: routePoint.description,
          trackClientId,
        })),
      )
      return
    }

    let nextInsertIndex = trackInsertIndex
    for (const routePoint of routePoints) {
      await insertTrackedRoutePoint(tourId, trackClientId, nextInsertIndex, routePoint)
      nextInsertIndex += 1
    }
  }

  const insertRoutePointsAtGlobalIndex = async (
    tourId: string,
    globalInsertIndex: number,
    trackClientId: string,
    routePoints: Array<
      Pick<RoutePoint, 'clientId' | 'lat' | 'lon' | 'ele' | 'name' | 'description'>
    >,
  ) => {
    const routePointInputs = routePoints.map((routePoint) => ({
      clientId: routePoint.clientId,
      lat: routePoint.lat,
      lon: routePoint.lon,
      ele: routePoint.ele,
      name: routePoint.name,
      description: routePoint.description,
      trackClientId,
    }))

    if (adapter.insertRoutePointsAt) {
      await adapter.insertRoutePointsAt(tourId, globalInsertIndex, routePointInputs)
      return
    }

    for (let offset = 0; offset < routePointInputs.length; offset += 1) {
      await adapter.insertRoutePointAt(tourId, globalInsertIndex + offset, routePointInputs[offset])
    }
  }

  const closeSparseTrackSetup = () => {
    sparseTrackSetup.value = null
  }

  const startSparseTrackApplyPreview = (
    tourId: string,
    trackClientId: string,
    routePoints: Array<Pick<RoutePoint, 'lat' | 'lon'>>,
  ) => {
    sparseTrackApplyState.value = {
      tourId,
      trackClientId,
      remainingPreviewRoutePoints: routePoints.map(({ lat, lon }) => ({ lat, lon })),
    }
  }

  const clearSparseTrackApplyPreview = () => {
    sparseTrackApplyState.value = null
  }

  const maybeOpenSparseTrackSetup = (): boolean => {
    const context = getEditingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length > 2) {
      closeSparseTrackSetup()
      return false
    }

    const lineString = getTrackInsertionLineString(context.track)
    if (!lineString) {
      showError('Track geometry is unavailable for helper-point setup')
      return false
    }

    const minPointCount = 1
    const maxPointCount = DEFAULT_SPARSE_TRACK_MAX_HELPER_POINTS
    const pointCount = getSuggestedSparseTrackHelperPointCount(lineString)

    sparseTrackSetup.value = {
      tourId: context.tourId,
      trackClientId: context.trackClientId,
      lineString,
      pointCount,
      minPointCount,
      maxPointCount,
      previewRoutePoints: toSparseTrackPreviewPoints(
        lineString,
        pointCount,
        minPointCount,
        maxPointCount,
      ),
    }

    return true
  }

  const updateSparseTrackPointCount = (value: number) => {
    const currentSetup = sparseTrackSetup.value
    if (!currentSetup) return

    const pointCount = clampSparseTrackPointCount(
      value,
      currentSetup.minPointCount,
      currentSetup.maxPointCount,
    )
    sparseTrackSetup.value = {
      ...currentSetup,
      pointCount,
      previewRoutePoints: toSparseTrackPreviewPoints(
        currentSetup.lineString,
        pointCount,
        currentSetup.minPointCount,
        currentSetup.maxPointCount,
      ),
    }
  }

  const confirmSparseTrackSetup = async (): Promise<boolean> => {
    const currentSetup = sparseTrackSetup.value
    if (!currentSetup) return false
    let shouldRestoreSparseTrackSetup = false
    const restoreSparseTrackSetup = () => {
      if (!shouldRestoreSparseTrackSetup) return
      const activeContext = getEditingTrackContext()
      if (
        !activeContext ||
        activeContext.tourId !== currentSetup.tourId ||
        activeContext.trackClientId !== currentSetup.trackClientId
      ) {
        return
      }
      sparseTrackSetup.value = currentSetup
    }
    try {
      isCalculatingRoute.value = true
      const context = getEditingTrackContext()
      if (!context) return false
      if (
        context.tourId !== currentSetup.tourId ||
        context.trackClientId !== currentSetup.trackClientId
      ) {
        closeSparseTrackSetup()
        return false
      }

      const trackRoutePointEntries = getTrackRoutePointEntries(
        context.tour.routePoints,
        context.trackClientId,
      )
      const trackRoutePoints = trackRoutePointEntries.map((entry) => entry.routePoint)
      if (trackRoutePoints.length > 2) {
        closeSparseTrackSetup()
        return true
      }

      const helperCoords = buildSparseTrackHelperCoords(
        currentSetup.lineString,
        currentSetup.pointCount,
        currentSetup.maxPointCount,
      )
      if (!helperCoords || helperCoords.length === 0) {
        showError('Track helper points could not be calculated')
        return false
      }

      const helperRoutePointSnapshots = helperCoords.map(([lon, lat]) => ({
        clientId: generateId(),
        lat,
        lon,
      }))
      const usesDerivedAnchors = trackRoutePoints.length < 2
      const lineCoords = usesDerivedAnchors ? parseLineString(currentSetup.lineString) : []
      if (usesDerivedAnchors && lineCoords.length < 2) {
        showError('Track helper points could not be calculated')
        return false
      }
      const derivedAnchorSnapshots = usesDerivedAnchors
        ? [lineCoords[0], lineCoords[lineCoords.length - 1]].map(([lon, lat]) => ({
            clientId: generateId(),
            lat,
            lon,
          }))
        : []
      const rebuiltTrackRoutePoints = usesDerivedAnchors
        ? [derivedAnchorSnapshots[0], ...helperRoutePointSnapshots, derivedAnchorSnapshots[1]]
        : [
            trackRoutePoints[0],
            ...helperRoutePointSnapshots,
            trackRoutePoints[trackRoutePoints.length - 1],
          ]
      const routePointsToInsert = usesDerivedAnchors
        ? rebuiltTrackRoutePoints
        : helperRoutePointSnapshots
      const replacedRoutePoints = usesDerivedAnchors ? cloneRoutePoints(trackRoutePoints) : []
      const globalInsertIndex =
        trackRoutePointEntries[0]?.globalIndex ?? context.tour.routePoints.length

      startSparseTrackApplyPreview(context.tourId, context.trackClientId, helperRoutePointSnapshots)
      shouldRestoreSparseTrackSetup = true
      closeSparseTrackSetup()
      await nextTick()

      setSpinningRoutePoints(rebuiltTrackRoutePoints)
      const rebuiltSegments = await rebuildAllSegments(
        rebuiltTrackRoutePoints,
        uiStore.routingProfile,
        {
          maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
        },
      )
      if (!rebuiltSegments) {
        restoreSparseTrackSetup()
        showError('Routing failed – helper points could not be applied')
        return false
      }

      const beforeState: SegmentState = {
        kind: 'full',
        segments: cloneSegments(context.track.segments),
        lineString: context.track.pointsLineReduced5m,
      }
      const afterState: SegmentState = {
        kind: 'full',
        segments: cloneSegments(rebuiltSegments),
        lineString: buildLineStringFromSegments(rebuiltSegments),
      }

      await runAdapterSyncBatch(adapter, async () => {
        await applySegmentState(context.tourId, context.trackClientId, afterState)
        shouldRestoreSparseTrackSetup = false
        if (usesDerivedAnchors) {
          for (const routePoint of replacedRoutePoints) {
            await deleteRoutePointByClientId(context.tourId, routePoint.clientId)
          }
          await insertRoutePointsAtGlobalIndex(
            context.tourId,
            globalInsertIndex,
            context.trackClientId,
            routePointsToInsert,
          )
        } else {
          await insertTrackedRoutePoints(
            context.tourId,
            context.trackClientId,
            1,
            routePointsToInsert,
          )
        }
      })

      pushEditHistoryEntry({
        undo: async () => {
          await runAdapterSyncBatch(adapter, async () => {
            await applySegmentState(context.tourId, context.trackClientId, beforeState)
            for (const routePoint of routePointsToInsert) {
              await deleteRoutePointByClientId(context.tourId, routePoint.clientId)
            }
            if (usesDerivedAnchors && replacedRoutePoints.length > 0) {
              await insertRoutePointsAtGlobalIndex(
                context.tourId,
                globalInsertIndex,
                context.trackClientId,
                replacedRoutePoints,
              )
            }
          })
        },
        redo: async () => {
          await runAdapterSyncBatch(adapter, async () => {
            await applySegmentState(context.tourId, context.trackClientId, afterState)
            if (usesDerivedAnchors) {
              for (const routePoint of replacedRoutePoints) {
                await deleteRoutePointByClientId(context.tourId, routePoint.clientId)
              }
              await insertRoutePointsAtGlobalIndex(
                context.tourId,
                globalInsertIndex,
                context.trackClientId,
                routePointsToInsert,
              )
            } else {
              await insertTrackedRoutePoints(
                context.tourId,
                context.trackClientId,
                1,
                routePointsToInsert,
              )
            }
          })
        },
      })

      refreshEditModeAnchor()
      return true
    } catch (error) {
      restoreSparseTrackSetup()
      logService.error(LOG_CATEGORIES.MAP, 'Failed to confirm sparse-track setup', { error })
      showError(
        `Failed to prepare track for editing: ${error instanceof Error ? error.message : String(error)}`,
      )
      return false
    } finally {
      clearSparseTrackApplyPreview()
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const performRoutePointMove = async (
    tourId: string,
    routePointIndex: number,
    newLat: number,
    newLon: number,
  ): Promise<boolean> => {
    const context = findEditingRoutePointContext(tourId, routePointIndex)
    if (!context) return false

    setNeighborSpinnerPoints(context.trackRoutePoints, context.trackLocalIndex, {
      lat: newLat,
      lon: newLon,
    })

    const previousRoutePoint = cloneRoutePoint(context.routePoint)
    const hasLocalSegments = hasCompleteLocalSegments(
      context.track.segments,
      context.trackRoutePoints,
    )
    let beforeState: SegmentState
    let afterState: SegmentState

    if (hasLocalSegments) {
      const result = await recalcSegmentsForMove(
        context.track.segments,
        context.trackRoutePoints,
        context.trackLocalIndex,
        newLat,
        newLon,
        uiStore.routingProfile,
      )
      if (!result) {
        showError('Routing failed – route point not moved')
        return false
      }

      beforeState = buildUndoRangeState(context.track.segments, result)
      afterState = {
        kind: 'range',
        deletedIndices: [...result.deletedIndices],
        newSegments: cloneRangeSegments(result.newSegments),
      }
    } else {
      const updatedTrackRoutePoints = context.trackRoutePoints.map((routePoint, index) =>
        index === context.trackLocalIndex
          ? { ...routePoint, lat: newLat, lon: newLon }
          : routePoint,
      )
      const rebuiltSegments = await rebuildAllSegments(
        updatedTrackRoutePoints,
        uiStore.routingProfile,
      )
      if (!rebuiltSegments) {
        showError('Routing failed – could not rebuild segments')
        return false
      }

      beforeState = {
        kind: 'full',
        segments: cloneSegments(context.track.segments),
        lineString: context.track.pointsLineReduced5m,
      }
      afterState = {
        kind: 'full',
        segments: cloneSegments(rebuiltSegments),
        lineString: buildLineStringFromSegments(rebuiltSegments),
      }
    }

    await applySegmentState(context.tourId, context.trackClientId, afterState)
    await adapter.updateRoutePointPosition(context.tourId, routePointIndex, newLat, newLon)

    pushEditHistoryEntry({
      undo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, beforeState)
        const currentIndex = findRoutePointIndexByClientId(
          context.tourId,
          previousRoutePoint.clientId,
        )
        if (currentIndex !== null) {
          await adapter.updateRoutePointPosition(
            context.tourId,
            currentIndex,
            previousRoutePoint.lat,
            previousRoutePoint.lon,
          )
        }
      },
      redo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, afterState)
        const currentIndex = findRoutePointIndexByClientId(
          context.tourId,
          previousRoutePoint.clientId,
        )
        if (currentIndex !== null) {
          await adapter.updateRoutePointPosition(context.tourId, currentIndex, newLat, newLon)
        }
      },
    })

    refreshEditModeAnchor()
    return true
  }

  const performRoutePointInsert = async (
    trackInsertIndex: number,
    lat: number,
    lon: number,
    options: { allowSparseDirectInsert?: boolean } = {},
  ): Promise<boolean> => {
    const context = getEditingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length <= 2 && !options.allowSparseDirectInsert) {
      maybeOpenSparseTrackSetup()
      return false
    }

    const globalInsertIndex = getTrackGlobalInsertIndex(
      context.tour.routePoints,
      context.trackClientId,
      trackInsertIndex,
    )
    if (globalInsertIndex === null) return false

    const snappedTarget = await snapInsertTarget(lat, lon)
    lat = snappedTarget.lat
    lon = snappedTarget.lon

    setSpinningRoutePoints([
      trackRoutePoints[trackInsertIndex - 1],
      { lat, lon },
      trackRoutePoints[trackInsertIndex],
    ])

    const currentLineString = getTrackInsertionLineString(context.track)
    const hasLocalSegments = hasCompleteLocalSegments(context.track.segments, trackRoutePoints)
    let beforeState: SegmentState
    let afterState: SegmentState

    if (hasLocalSegments) {
      // Full BRouter re-route of the two sections around the drop
      // (prev anchor → drop, drop → next anchor).
      let result = await recalcSegmentsForInsert(
        trackRoutePoints,
        trackInsertIndex,
        lat,
        lon,
        uiStore.routingProfile,
      )
      if (!result) {
        const fallback = buildInsertGeometryFallbackSegments(
          context.track.segments,
          trackInsertIndex,
          lat,
          lon,
        )
        if (!fallback) {
          showError('Routing failed – route point not inserted')
          return false
        }
        logService.warn(
          LOG_CATEGORIES.MAP,
          'Insert reroute unavailable; kept geometry with a straight leg to the drop',
          { trackInsertIndex },
        )
        lat = fallback.insertedLat
        lon = fallback.insertedLon
        result = { deletedIndices: fallback.deletedIndices, newSegments: fallback.newSegments }
      }

      beforeState = buildUndoRangeState(context.track.segments, result)
      afterState = {
        kind: 'range',
        deletedIndices: [...result.deletedIndices],
        newSegments: cloneRangeSegments(result.newSegments),
      }
    } else {
      // Geometry-only track (no local segments): re-route every section through
      // the inserted point via BRouter.
      const updatedTrackRoutePoints = [...trackRoutePoints]
      updatedTrackRoutePoints.splice(trackInsertIndex, 0, {
        clientId: '__pending__',
        lat,
        lon,
        order: trackInsertIndex,
        trackClientId: context.trackClientId,
      })
      const rebuiltSegments = await rebuildAllSegments(
        updatedTrackRoutePoints,
        uiStore.routingProfile,
      )
      if (rebuiltSegments) {
        beforeState = {
          kind: 'full',
          segments: cloneSegments(context.track.segments),
          lineString: currentLineString,
        }
        afterState = {
          kind: 'full',
          segments: cloneSegments(rebuiltSegments),
          lineString: buildLineStringFromSegments(rebuiltSegments),
        }
      } else {
        const fallback = materializeInsertedGeometryOnlySegments(currentLineString, lat, lon)
        if (!fallback) {
          showError('Routing failed – could not rebuild segments')
          return false
        }
        logService.warn(
          LOG_CATEGORIES.MAP,
          'Insert reroute unavailable; kept geometry with a straight leg to the drop',
          { trackInsertIndex },
        )
        lat = fallback.insertedLat
        lon = fallback.insertedLon
        beforeState = {
          kind: 'full',
          segments: cloneSegments(context.track.segments),
          lineString: currentLineString,
        }
        afterState = {
          kind: 'full',
          segments: cloneSegments(fallback.segments),
          lineString: buildLineStringFromSegments(fallback.segments),
        }
      }
    }

    const routePointSnapshot: Pick<
      RoutePoint,
      'clientId' | 'lat' | 'lon' | 'ele' | 'name' | 'description'
    > = {
      clientId: generateId(),
      lat,
      lon,
    }

    await applySegmentState(context.tourId, context.trackClientId, afterState)
    await adapter.insertRoutePointAt(context.tourId, globalInsertIndex, {
      clientId: routePointSnapshot.clientId,
      lat,
      lon,
      trackClientId: context.trackClientId,
    })

    pushEditHistoryEntry({
      undo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, beforeState)
        await deleteRoutePointByClientId(context.tourId, routePointSnapshot.clientId)
      },
      redo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, afterState)
        await insertTrackedRoutePoint(
          context.tourId,
          context.trackClientId,
          trackInsertIndex,
          routePointSnapshot,
        )
      },
    })

    refreshEditModeAnchor()
    return true
  }

  const performRoutePointDelete = async (
    tourId: string,
    routePointIndex: number,
  ): Promise<boolean> => {
    const context = findEditingRoutePointContext(tourId, routePointIndex)
    if (!context) return false

    if (context.trackRoutePoints.length === 1) {
      showError('The last route point cannot be deleted in route edit mode')
      return false
    }

    const deletedRoutePoint = cloneRoutePoint(context.routePoint)
    const hasLocalSegments = hasCompleteLocalSegments(
      context.track.segments,
      context.trackRoutePoints,
    )
    let beforeState: SegmentState
    let afterState: SegmentState

    if (hasLocalSegments) {
      const result = await recalcSegmentsForDelete(
        context.track.segments,
        context.trackRoutePoints,
        context.trackLocalIndex,
        uiStore.routingProfile,
      )
      if (!result) {
        showError('Routing failed – could not update route after deletion')
        return false
      }

      beforeState = buildUndoRangeState(context.track.segments, result)
      afterState = {
        kind: 'range',
        deletedIndices: [...result.deletedIndices],
        newSegments: cloneRangeSegments(result.newSegments),
      }
    } else {
      const updatedTrackRoutePoints = context.trackRoutePoints.filter(
        (_, index) => index !== context.trackLocalIndex,
      )
      const rebuiltSegments = await rebuildAllSegments(
        updatedTrackRoutePoints,
        uiStore.routingProfile,
      )
      if (rebuiltSegments === null) {
        showError('Routing failed – could not rebuild segments')
        return false
      }

      beforeState = {
        kind: 'full',
        segments: cloneSegments(context.track.segments),
        lineString: context.track.pointsLineReduced5m,
      }
      afterState = {
        kind: 'full',
        segments: cloneSegments(rebuiltSegments),
        lineString: buildLineStringFromSegments(rebuiltSegments),
      }
    }

    await applySegmentState(context.tourId, context.trackClientId, afterState)
    await adapter.deleteRoutePoint(context.tourId, routePointIndex)

    pushEditHistoryEntry({
      undo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, beforeState)
        await insertTrackedRoutePoint(
          context.tourId,
          context.trackClientId,
          context.trackLocalIndex,
          deletedRoutePoint,
        )
      },
      redo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, afterState)
        await deleteRoutePointByClientId(context.tourId, deletedRoutePoint.clientId)
      },
    })

    refreshEditModeAnchor()
    return true
  }

  const performAppendOrPrepend = async (event: MapMouseEvent): Promise<boolean> => {
    const context = getEditingTrackContext()
    if (!context) return false

    const direction = uiStore.editingTrackAppendDirection
    const lat = event.lngLat.lat
    const lon = event.lngLat.lng
    setSpinningRoutePoints([
      lastClickedPoint.value
        ? { lat: lastClickedPoint.value[0], lon: lastClickedPoint.value[1] }
        : null,
      { lat, lon },
    ])

    const snapped = await routingService.getNearest(lat, lon)
    const targetPoint = snapped || [lat, lon]

    if (!lastClickedPoint.value) {
      logService.error(LOG_CATEGORIES.MAP, 'No last clicked point for track edit')
      return false
    }

    const routePoints =
      direction === 'end'
        ? [lastClickedPoint.value, targetPoint]
        : [targetPoint, lastClickedPoint.value]

    const route = await routingService.getRoute(routePoints, uiStore.routingProfile)
    if (!route) {
      if (!uiStore.isTrackEditMode) return false
      showError('Routing failed – please try a different point')
      return false
    }

    const segmentSnapshot = routeResultToSegmentWithTime(route, Date.now())
    if (segmentSnapshot.points.length === 0) return false

    const routePointSnapshot: Pick<
      RoutePoint,
      'clientId' | 'lat' | 'lon' | 'ele' | 'name' | 'description'
    > = {
      clientId: generateId(),
      lat: targetPoint[0],
      lon: targetPoint[1],
    }

    const segmentOrder = direction === 'end' ? context.track.segments.length : 0
    const trackInsertIndex =
      direction === 'end'
        ? getTrackRoutePoints(context.tour.routePoints, context.trackClientId).length
        : 0
    const beforeLineCoords = context.track.pointsLineReduced5m
      ? parseLineString(context.track.pointsLineReduced5m)
      : []
    const newCoords: [number, number][] = segmentSnapshot.points.map((point) => [
      point.lon,
      point.lat,
    ])
    const mergedCoords =
      direction === 'end'
        ? [...beforeLineCoords, ...newCoords]
        : [...newCoords, ...beforeLineCoords]
    const afterLineString =
      mergedCoords.length >= 2
        ? `LINESTRING(${mergedCoords.map((coord) => `${coord[0]} ${coord[1]}`).join(', ')})`
        : context.track.pointsLineReduced5m

    const redoSegmentState: SegmentState = {
      kind: 'range',
      deletedIndices: [],
      newSegments: [{ segment: cloneSegment(segmentSnapshot), order: segmentOrder }],
      lineString: afterLineString,
    }
    const undoSegmentState: SegmentState = {
      kind: 'range',
      deletedIndices: [segmentOrder],
      newSegments: [],
      lineString: context.track.pointsLineReduced5m,
    }

    await applySegmentState(context.tourId, context.trackClientId, redoSegmentState)
    await insertTrackedRoutePoint(
      context.tourId,
      context.trackClientId,
      trackInsertIndex,
      routePointSnapshot,
    )

    pushEditHistoryEntry({
      undo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, undoSegmentState)
        await deleteRoutePointByClientId(context.tourId, routePointSnapshot.clientId)
      },
      redo: async () => {
        await applySegmentState(context.tourId, context.trackClientId, redoSegmentState)
        await insertTrackedRoutePoint(
          context.tourId,
          context.trackClientId,
          trackInsertIndex,
          routePointSnapshot,
        )
      },
    })

    lastClickedPoint.value = targetPoint
    return true
  }

  const { handlePendingDeleted, handlePendingDragend } = usePendingRoutePointOps({
    drawingTrackSegments,
    drawingTrackPoints,
    lastClickedPoint,
    editModeNewSegments,
    isCalculatingRoute,
    onError: (message) => showError(message),
  })

  const handleTrackEditClick = async (event: MapMouseEvent) => {
    if (shouldSuppressInsertDragClick()) return
    if (isSparseTrackSetupOpen.value) return

    try {
      isCalculatingRoute.value = true
      await performAppendOrPrepend(event)
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error adding track edit point', { error })
      showError('Failed to add track point')
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const handleRoutePointDragend = async (
    tourId: string,
    routePointIndex: number,
    newLat: number,
    newLon: number,
  ) => {
    try {
      isCalculatingRoute.value = true
      if (uiStore.isTrackEditMode) {
        await performRoutePointMove(tourId, routePointIndex, newLat, newLon)
        return
      }

      if (uiStore.isTrackDrawingActive) {
        const context = findDrawingRoutePointContext(tourId, routePointIndex)
        if (!context) return
        setNeighborSpinnerPoints(context.trackRoutePoints, context.trackLocalIndex, {
          lat: newLat,
          lon: newLon,
        })

        const previousRoutePoint = cloneRoutePoint(context.routePoint)
        const beforeDrawingState = snapshotDrawingLocalState()
        const hasLocalSegments = hasCompleteLocalSegments(
          context.track.segments,
          context.trackRoutePoints,
        )
        let beforeState: SegmentState
        let afterState: SegmentState

        if (hasLocalSegments) {
          const result = await recalcSegmentsForMove(
            context.track.segments,
            context.trackRoutePoints,
            context.trackLocalIndex,
            newLat,
            newLon,
            uiStore.routingProfile,
          )
          if (!result) {
            showError('Routing failed – route point not moved')
            return
          }

          beforeState = buildUndoRangeState(context.track.segments, result)
          afterState = {
            kind: 'range',
            deletedIndices: [...result.deletedIndices],
            newSegments: cloneRangeSegments(result.newSegments),
          }
        } else {
          const updatedTrackRoutePoints = context.trackRoutePoints.map((routePoint, index) =>
            index === context.trackLocalIndex
              ? { ...routePoint, lat: newLat, lon: newLon }
              : routePoint,
          )
          const rebuiltSegments = await rebuildAllSegments(
            updatedTrackRoutePoints,
            uiStore.routingProfile,
          )
          if (!rebuiltSegments) {
            showError('Routing failed – could not rebuild segments')
            return
          }

          beforeState = {
            kind: 'full',
            segments: cloneSegments(context.track.segments),
            lineString: context.track.pointsLineReduced5m,
          }
          afterState = {
            kind: 'full',
            segments: cloneSegments(rebuiltSegments),
            lineString: buildLineStringFromSegments(rebuiltSegments),
          }
        }

        const updatedTrackRoutePoints = context.trackRoutePoints.map((routePoint, index) =>
          index === context.trackLocalIndex
            ? { ...routePoint, lat: newLat, lon: newLon }
            : routePoint,
        )
        const afterDrawingState = buildDrawingLocalStateFromTrack(
          context.trackClientId,
          updatedTrackRoutePoints,
          materializeSegments(context.track.segments, afterState),
        )

        await applySegmentState(context.tourId, context.trackClientId, afterState)
        await adapter.updateRoutePointPosition(context.tourId, routePointIndex, newLat, newLon)
        applyDrawingLocalState(afterDrawingState)

        pushDrawingHistoryEntry({
          undo: async () => {
            await applySegmentState(context.tourId, context.trackClientId, beforeState)
            const currentIndex = findRoutePointIndexByClientId(
              context.tourId,
              previousRoutePoint.clientId,
            )
            if (currentIndex !== null) {
              await adapter.updateRoutePointPosition(
                context.tourId,
                currentIndex,
                previousRoutePoint.lat,
                previousRoutePoint.lon,
              )
            }
            applyDrawingLocalState(beforeDrawingState)
          },
          redo: async () => {
            await applySegmentState(context.tourId, context.trackClientId, afterState)
            const currentIndex = findRoutePointIndexByClientId(
              context.tourId,
              previousRoutePoint.clientId,
            )
            if (currentIndex !== null) {
              await adapter.updateRoutePointPosition(context.tourId, currentIndex, newLat, newLon)
            }
            applyDrawingLocalState(afterDrawingState)
          },
        })
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error moving route point during track edit', {
        error,
        tourId,
        routePointIndex,
        newLat,
        newLon,
      })
      showError(
        `Failed to move route point: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const handleTrackEditInsertDrop = async (trackInsertIndex: number, lat: number, lon: number) => {
    if (isSparseTrackSetupOpen.value) return

    try {
      isCalculatingRoute.value = true
      await performRoutePointInsert(trackInsertIndex, lat, lon, { allowSparseDirectInsert: true })
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error inserting route point during track edit', {
        error,
        trackInsertIndex,
        lat,
        lon,
      })
      showError(
        `Failed to insert route point: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const handleDrawingTrackInsertDrop = async (
    trackInsertIndex: number,
    lat: number,
    lon: number,
  ) => {
    try {
      isCalculatingRoute.value = true

      const context = getDrawingTrackContext()
      if (!context) return

      const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
      if (trackInsertIndex < 1 || trackInsertIndex > trackRoutePoints.length - 1) {
        return
      }

      const snappedTarget = await snapInsertTarget(lat, lon)
      lat = snappedTarget.lat
      lon = snappedTarget.lon

      setSpinningRoutePoints([
        trackRoutePoints[trackInsertIndex - 1],
        { lat, lon },
        trackRoutePoints[trackInsertIndex],
      ])

      const beforeDrawingState = snapshotDrawingLocalState()
      const currentLineString = getTrackInsertionLineString(context.track)
      const hasLocalSegments = hasCompleteLocalSegments(context.track.segments, trackRoutePoints)
      let beforeState: SegmentState
      let afterState: SegmentState

      if (hasLocalSegments) {
        // Full BRouter re-route of the two sections around the drop
        // (prev anchor → drop, drop → next anchor).
        let result = await recalcSegmentsForInsert(
          trackRoutePoints,
          trackInsertIndex,
          lat,
          lon,
          uiStore.routingProfile,
        )
        if (!result) {
          const fallback = buildInsertGeometryFallbackSegments(
            context.track.segments,
            trackInsertIndex,
            lat,
            lon,
          )
          if (!fallback) {
            showError('Routing failed – route point not inserted')
            return
          }
          logService.warn(
            LOG_CATEGORIES.MAP,
            'Insert reroute unavailable; kept geometry with a straight leg to the drop',
            { trackInsertIndex },
          )
          lat = fallback.insertedLat
          lon = fallback.insertedLon
          result = { deletedIndices: fallback.deletedIndices, newSegments: fallback.newSegments }
        }

        beforeState = buildUndoRangeState(context.track.segments, result)
        afterState = {
          kind: 'range',
          deletedIndices: [...result.deletedIndices],
          newSegments: cloneRangeSegments(result.newSegments),
        }
      } else {
        // Geometry-only track (no local segments): re-route every section through
        // the inserted point via BRouter.
        const updatedTrackRoutePoints = [...trackRoutePoints]
        updatedTrackRoutePoints.splice(trackInsertIndex, 0, {
          clientId: '__pending__',
          lat,
          lon,
          order: trackInsertIndex,
          trackClientId: context.trackClientId,
        })
        const rebuiltSegments = await rebuildAllSegments(
          updatedTrackRoutePoints,
          uiStore.routingProfile,
        )
        if (rebuiltSegments) {
          beforeState = {
            kind: 'full',
            segments: cloneSegments(context.track.segments),
            lineString: currentLineString,
          }
          afterState = {
            kind: 'full',
            segments: cloneSegments(rebuiltSegments),
            lineString: buildLineStringFromSegments(rebuiltSegments),
          }
        } else {
          const fallback = materializeInsertedGeometryOnlySegments(currentLineString, lat, lon)
          if (!fallback) {
            showError('Routing failed – could not rebuild segments')
            return
          }
          logService.warn(
            LOG_CATEGORIES.MAP,
            'Insert reroute unavailable; kept geometry with a straight leg to the drop',
            { trackInsertIndex },
          )
          lat = fallback.insertedLat
          lon = fallback.insertedLon
          beforeState = {
            kind: 'full',
            segments: cloneSegments(context.track.segments),
            lineString: currentLineString,
          }
          afterState = {
            kind: 'full',
            segments: cloneSegments(fallback.segments),
            lineString: buildLineStringFromSegments(fallback.segments),
          }
        }
      }

      const routePointSnapshot: Pick<
        RoutePoint,
        'clientId' | 'lat' | 'lon' | 'ele' | 'name' | 'description'
      > = {
        clientId: generateId(),
        lat,
        lon,
      }
      const updatedTrackRoutePoints = [...trackRoutePoints]
      updatedTrackRoutePoints.splice(trackInsertIndex, 0, {
        clientId: routePointSnapshot.clientId,
        lat,
        lon,
        order: trackInsertIndex,
        trackClientId: context.trackClientId,
      })
      const afterDrawingState = buildDrawingLocalStateFromTrack(
        context.trackClientId,
        updatedTrackRoutePoints,
        materializeSegments(context.track.segments, afterState),
      )

      await applySegmentState(context.tourId, context.trackClientId, afterState)
      await insertTrackedRoutePoint(
        context.tourId,
        context.trackClientId,
        trackInsertIndex,
        routePointSnapshot,
      )
      applyDrawingLocalState(afterDrawingState)

      pushDrawingHistoryEntry({
        undo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          await deleteRoutePointByClientId(context.tourId, routePointSnapshot.clientId)
          applyDrawingLocalState(beforeDrawingState)
        },
        redo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          await insertTrackedRoutePoint(
            context.tourId,
            context.trackClientId,
            trackInsertIndex,
            routePointSnapshot,
          )
          applyDrawingLocalState(afterDrawingState)
        },
      })
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error inserting route point during track drawing', {
        error,
        trackInsertIndex,
        lat,
        lon,
      })
      showError(
        `Failed to insert route point: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const deleteRoutePointInEditMode = async (
    tourId: string,
    routePointIndex: number,
  ): Promise<boolean> => {
    if (isCalculatingRoute.value) return false

    try {
      isCalculatingRoute.value = true
      const context = findEditingRoutePointContext(tourId, routePointIndex)
      if (!context) return false
      setNeighborSpinnerPoints(context.trackRoutePoints, context.trackLocalIndex)
      return await performRoutePointDelete(tourId, routePointIndex)
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error deleting route point during track edit', {
        error,
        tourId,
        routePointIndex,
      })
      showError(
        `Failed to delete route point: ${error instanceof Error ? error.message : String(error)}`,
      )
      return false
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  // Delete a route point while *drawing* a new track. Mirrors performRoutePointDelete (edit mode)
  // but operates on the in-memory drawing draft state + drawing undo stack, so deleting a via-point
  // mid-draw re-routes the neighbours and stays undoable, without emitting persistence events.
  const deleteRoutePointInDrawingMode = async (
    tourId: string,
    routePointIndex: number,
  ): Promise<boolean> => {
    if (isCalculatingRoute.value) return false

    try {
      isCalculatingRoute.value = true
      const context = findDrawingRoutePointContext(tourId, routePointIndex)
      if (!context) return false

      if (context.trackRoutePoints.length <= 2) {
        showError('A route needs at least a start and an end point')
        return false
      }

      setNeighborSpinnerPoints(context.trackRoutePoints, context.trackLocalIndex)

      const deletedRoutePoint = cloneRoutePoint(context.routePoint)
      const beforeDrawingState = snapshotDrawingLocalState()
      const hasLocalSegments = hasCompleteLocalSegments(
        context.track.segments,
        context.trackRoutePoints,
      )
      let beforeState: SegmentState
      let afterState: SegmentState

      if (hasLocalSegments) {
        const result = await recalcSegmentsForDelete(
          context.track.segments,
          context.trackRoutePoints,
          context.trackLocalIndex,
          uiStore.routingProfile,
        )
        if (!result) {
          showError('Routing failed – could not update route after deletion')
          return false
        }

        beforeState = buildUndoRangeState(context.track.segments, result)
        afterState = {
          kind: 'range',
          deletedIndices: [...result.deletedIndices],
          newSegments: cloneRangeSegments(result.newSegments),
        }
      } else {
        const updatedTrackRoutePoints = context.trackRoutePoints.filter(
          (_, index) => index !== context.trackLocalIndex,
        )
        const rebuiltSegments = await rebuildAllSegments(
          updatedTrackRoutePoints,
          uiStore.routingProfile,
        )
        if (rebuiltSegments === null) {
          showError('Routing failed – could not rebuild segments')
          return false
        }

        beforeState = {
          kind: 'full',
          segments: cloneSegments(context.track.segments),
          lineString: context.track.pointsLineReduced5m,
        }
        afterState = {
          kind: 'full',
          segments: cloneSegments(rebuiltSegments),
          lineString: buildLineStringFromSegments(rebuiltSegments),
        }
      }

      const updatedTrackRoutePoints = context.trackRoutePoints.filter(
        (_, index) => index !== context.trackLocalIndex,
      )
      const afterDrawingState = buildDrawingLocalStateFromTrack(
        context.trackClientId,
        updatedTrackRoutePoints,
        materializeSegments(context.track.segments, afterState),
      )

      await applySegmentState(context.tourId, context.trackClientId, afterState)
      await adapter.deleteRoutePoint(context.tourId, routePointIndex)
      applyDrawingLocalState(afterDrawingState)

      pushDrawingHistoryEntry({
        undo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          await insertTrackedRoutePoint(
            context.tourId,
            context.trackClientId,
            context.trackLocalIndex,
            deletedRoutePoint,
          )
          applyDrawingLocalState(beforeDrawingState)
        },
        redo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          await deleteRoutePointByClientId(context.tourId, deletedRoutePoint.clientId)
          applyDrawingLocalState(afterDrawingState)
        },
      })

      return true
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Error deleting route point during track drawing', {
        error,
        tourId,
        routePointIndex,
      })
      showError(
        `Failed to delete route point: ${error instanceof Error ? error.message : String(error)}`,
      )
      return false
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const unregisterSessionHandler = registerRouteEditSessionHandler({
    canHandleRoutePointDelete: (tourId, routePointIndex) =>
      !!findEditingRoutePointContext(tourId, routePointIndex) ||
      !!findDrawingRoutePointContext(tourId, routePointIndex),
    deleteRoutePoint: (tourId, routePointIndex) =>
      findEditingRoutePointContext(tourId, routePointIndex)
        ? deleteRoutePointInEditMode(tourId, routePointIndex)
        : deleteRoutePointInDrawingMode(tourId, routePointIndex),
  })
  const stopAppendDirectionWatcher = watch(
    () => uiStore.editingTrackAppendDirection,
    () => {
      if (!uiStore.isTrackEditMode) return
      refreshEditModeAnchor()
    },
    { flush: 'sync' },
  )
  const pendingProfileReroute = ref<ProfileRerouteHistoryState | null>(null)
  const isApplyingProfileHistory = ref(false)

  const setRoutingProfileWithoutReroute = (profile: string) => {
    isApplyingProfileHistory.value = true
    try {
      uiStore.setRoutingProfile(profile)
    } finally {
      isApplyingProfileHistory.value = false
    }
  }

  const rerouteEditingTrackForCurrentProfile = async (
    profileHistoryState?: ProfileRerouteHistoryState,
  ): Promise<boolean> => {
    const context = getEditingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length < 2) return false

    const beforeState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(context.track.segments),
      lineString: context.track.pointsLineReduced5m,
    }

    const rebuiltSegments = await rebuildAllSegments(trackRoutePoints, uiStore.routingProfile, {
      maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
    })
    if (!rebuiltSegments) {
      showError('Routing failed – could not update route for the selected profile')
      return true
    }

    const afterState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(rebuiltSegments),
      lineString: buildLineStringFromSegments(rebuiltSegments),
    }

    await applySegmentState(context.tourId, context.trackClientId, afterState)
    if (
      profileHistoryState &&
      profileHistoryState.previousProfile !== profileHistoryState.nextProfile
    ) {
      pushEditHistoryEntry({
        undo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          setRoutingProfileWithoutReroute(profileHistoryState.previousProfile)
        },
        redo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          setRoutingProfileWithoutReroute(profileHistoryState.nextProfile)
        },
      })
    }
    refreshEditModeAnchor()
    return true
  }

  const rerouteDrawingTrackForCurrentProfile = async (
    profileHistoryState?: ProfileRerouteHistoryState,
  ): Promise<boolean> => {
    const context = getDrawingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length < 2) return false

    const beforeState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(context.track.segments),
      lineString: context.track.pointsLineReduced5m,
    }
    const beforeDrawingState = snapshotDrawingLocalState()

    const rebuiltSegments = await rebuildAllSegments(trackRoutePoints, uiStore.routingProfile, {
      maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
    })
    if (!rebuiltSegments) {
      showError('Routing failed – could not update route for the selected profile')
      return true
    }

    const afterState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(rebuiltSegments),
      lineString: buildLineStringFromSegments(rebuiltSegments),
    }
    const afterDrawingState = buildDrawingLocalStateFromTrack(
      context.trackClientId,
      trackRoutePoints,
      rebuiltSegments,
    )

    await applySegmentState(context.tourId, context.trackClientId, afterState)
    applyDrawingLocalState(afterDrawingState)
    if (
      profileHistoryState &&
      profileHistoryState.previousProfile !== profileHistoryState.nextProfile
    ) {
      pushDrawingHistoryEntry({
        undo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          applyDrawingLocalState(beforeDrawingState)
          setRoutingProfileWithoutReroute(profileHistoryState.previousProfile)
        },
        redo: async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          applyDrawingLocalState(afterDrawingState)
          setRoutingProfileWithoutReroute(profileHistoryState.nextProfile)
        },
      })
    }
    return true
  }

  const reverseEditingTrackRoute = async (): Promise<boolean> => {
    const context = getEditingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length < 2) return false

    const reversedTrackRoutePoints = [...trackRoutePoints].reverse()
    const previousRoutePointClientIds = trackRoutePoints.map((routePoint) => routePoint.clientId)
    const reversedRoutePointClientIds = reversedTrackRoutePoints.map(
      (routePoint) => routePoint.clientId,
    )
    const rebuiltSegments = await rebuildAllSegments(
      reversedTrackRoutePoints,
      uiStore.routingProfile,
      {
        maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
      },
    )
    if (!rebuiltSegments) {
      showError('Routing failed – could not reverse the route')
      return true
    }

    const beforeState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(context.track.segments),
      lineString: context.track.pointsLineReduced5m,
    }
    const afterState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(rebuiltSegments),
      lineString: buildLineStringFromSegments(rebuiltSegments),
    }

    await runAdapterSyncBatch(adapter, async () => {
      await applySegmentState(context.tourId, context.trackClientId, afterState)
      await reorderTrackRoutePoints(
        context.tourId,
        context.trackClientId,
        reversedRoutePointClientIds,
      )
    })

    pushEditHistoryEntry({
      undo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            previousRoutePointClientIds,
          )
        })
      },
      redo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            reversedRoutePointClientIds,
          )
        })
      },
    })

    refreshEditModeAnchor()
    return true
  }

  const reverseDrawingTrackRoute = async (): Promise<boolean> => {
    const context = getDrawingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length < 2) return false

    const reversedTrackRoutePoints = [...trackRoutePoints].reverse()
    const previousRoutePointClientIds = trackRoutePoints.map((routePoint) => routePoint.clientId)
    const reversedRoutePointClientIds = reversedTrackRoutePoints.map(
      (routePoint) => routePoint.clientId,
    )
    const rebuiltSegments = await rebuildAllSegments(
      reversedTrackRoutePoints,
      uiStore.routingProfile,
      {
        maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
      },
    )
    if (!rebuiltSegments) {
      showError('Routing failed – could not reverse the route')
      return true
    }

    const beforeState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(context.track.segments),
      lineString: context.track.pointsLineReduced5m,
    }
    const afterState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(rebuiltSegments),
      lineString: buildLineStringFromSegments(rebuiltSegments),
    }
    const beforeDrawingState = snapshotDrawingLocalState()
    const afterDrawingState = buildDrawingLocalStateFromTrack(
      context.trackClientId,
      reversedTrackRoutePoints,
      rebuiltSegments,
    )

    await runAdapterSyncBatch(adapter, async () => {
      await applySegmentState(context.tourId, context.trackClientId, afterState)
      await reorderTrackRoutePoints(
        context.tourId,
        context.trackClientId,
        reversedRoutePointClientIds,
      )
    })
    applyDrawingLocalState(afterDrawingState)

    pushDrawingHistoryEntry({
      undo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            previousRoutePointClientIds,
          )
        })
        applyDrawingLocalState(beforeDrawingState)
      },
      redo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            reversedRoutePointClientIds,
          )
        })
        applyDrawingLocalState(afterDrawingState)
      },
    })

    return true
  }

  // Resolve the route-point objects for a track in the order given by `orderedRoutePointClientIds`.
  // Returns null when the supplied ids are not a permutation of the track's current route points
  // (stale drag drop) so the caller can bail without mutating anything.
  const resolveReorderedRoutePoints = (
    trackRoutePoints: RoutePoint[],
    orderedRoutePointClientIds: string[],
  ): RoutePoint[] | null => {
    if (orderedRoutePointClientIds.length !== trackRoutePoints.length) return null
    const byClientId = new Map(trackRoutePoints.map((rp) => [rp.clientId, rp]))
    const reordered: RoutePoint[] = []
    for (const clientId of orderedRoutePointClientIds) {
      const rp = byClientId.get(clientId)
      if (!rp) return null
      reordered.push(rp)
    }
    // Identity reorder (nothing actually moved) → treat as a no-op.
    if (reordered.every((rp, index) => rp.clientId === trackRoutePoints[index].clientId))
      return null
    return reordered
  }

  // Reorder the editing track's route points to a new client-id order, re-route every segment and
  // push an undo entry. Mirrors reverseEditingTrackRoute (reverse is just one specific reorder).
  const reorderEditingTrackRoute = async (
    orderedRoutePointClientIds: string[],
  ): Promise<boolean> => {
    const context = getEditingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length < 2) return false

    const reorderedTrackRoutePoints = resolveReorderedRoutePoints(
      trackRoutePoints,
      orderedRoutePointClientIds,
    )
    if (!reorderedTrackRoutePoints) return false

    const previousRoutePointClientIds = trackRoutePoints.map((routePoint) => routePoint.clientId)
    const nextRoutePointClientIds = reorderedTrackRoutePoints.map(
      (routePoint) => routePoint.clientId,
    )
    const rebuiltSegments = await rebuildAllSegments(
      reorderedTrackRoutePoints,
      uiStore.routingProfile,
      {
        maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
      },
    )
    if (!rebuiltSegments) {
      showError('Routing failed – could not reorder the route')
      return true
    }

    const beforeState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(context.track.segments),
      lineString: context.track.pointsLineReduced5m,
    }
    const afterState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(rebuiltSegments),
      lineString: buildLineStringFromSegments(rebuiltSegments),
    }

    await runAdapterSyncBatch(adapter, async () => {
      await applySegmentState(context.tourId, context.trackClientId, afterState)
      await reorderTrackRoutePoints(context.tourId, context.trackClientId, nextRoutePointClientIds)
    })

    pushEditHistoryEntry({
      undo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            previousRoutePointClientIds,
          )
        })
      },
      redo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            nextRoutePointClientIds,
          )
        })
      },
    })

    refreshEditModeAnchor()
    return true
  }

  // Reorder the drawing draft's route points. Mirrors reverseDrawingTrackRoute but for an arbitrary
  // new order, operating on the in-memory drawing draft state + drawing undo stack.
  const reorderDrawingTrackRoute = async (
    orderedRoutePointClientIds: string[],
  ): Promise<boolean> => {
    const context = getDrawingTrackContext()
    if (!context) return false

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    if (trackRoutePoints.length < 2) return false

    const reorderedTrackRoutePoints = resolveReorderedRoutePoints(
      trackRoutePoints,
      orderedRoutePointClientIds,
    )
    if (!reorderedTrackRoutePoints) return false

    const previousRoutePointClientIds = trackRoutePoints.map((routePoint) => routePoint.clientId)
    const nextRoutePointClientIds = reorderedTrackRoutePoints.map(
      (routePoint) => routePoint.clientId,
    )
    const rebuiltSegments = await rebuildAllSegments(
      reorderedTrackRoutePoints,
      uiStore.routingProfile,
      {
        maxConcurrency: CONFIG.BROUTER_MAX_CONCURRENT_REQUESTS,
      },
    )
    if (!rebuiltSegments) {
      showError('Routing failed – could not reorder the route')
      return true
    }

    const beforeState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(context.track.segments),
      lineString: context.track.pointsLineReduced5m,
    }
    const afterState: SegmentState = {
      kind: 'full',
      segments: cloneSegments(rebuiltSegments),
      lineString: buildLineStringFromSegments(rebuiltSegments),
    }
    const beforeDrawingState = snapshotDrawingLocalState()
    const afterDrawingState = buildDrawingLocalStateFromTrack(
      context.trackClientId,
      reorderedTrackRoutePoints,
      rebuiltSegments,
    )

    await runAdapterSyncBatch(adapter, async () => {
      await applySegmentState(context.tourId, context.trackClientId, afterState)
      await reorderTrackRoutePoints(context.tourId, context.trackClientId, nextRoutePointClientIds)
    })
    applyDrawingLocalState(afterDrawingState)

    pushDrawingHistoryEntry({
      undo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, beforeState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            previousRoutePointClientIds,
          )
        })
        applyDrawingLocalState(beforeDrawingState)
      },
      redo: async () => {
        await runAdapterSyncBatch(adapter, async () => {
          await applySegmentState(context.tourId, context.trackClientId, afterState)
          await reorderTrackRoutePoints(
            context.tourId,
            context.trackClientId,
            nextRoutePointClientIds,
          )
        })
        applyDrawingLocalState(afterDrawingState)
      },
    })

    return true
  }

  // Public entry point for the route-point list's drag-reorder: dispatches to the edit or drawing
  // variant (mirrors runReverseRoute), with spinner + busy handling.
  const runReorderRoute = async (orderedRoutePointClientIds: string[]) => {
    if (isCalculatingRoute.value) return

    try {
      isCalculatingRoute.value = true

      const editingContext = getEditingTrackContext()
      if (editingContext) {
        setSpinningRoutePoints(
          getTrackRoutePoints(editingContext.tour.routePoints, editingContext.trackClientId),
        )
        await reorderEditingTrackRoute(orderedRoutePointClientIds)
        return
      }

      const drawingContext = getDrawingTrackContext()
      if (!drawingContext) return
      setSpinningRoutePoints(
        getTrackRoutePoints(drawingContext.tour.routePoints, drawingContext.trackClientId),
      )
      await reorderDrawingTrackRoute(orderedRoutePointClientIds)
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Failed to reorder route points', { error })
      showError(
        `Failed to reorder route points: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const rerouteActiveTrackForCurrentProfile = async (
    profileHistoryState?: ProfileRerouteHistoryState,
  ) => {
    if (await rerouteEditingTrackForCurrentProfile(profileHistoryState)) return
    await rerouteDrawingTrackForCurrentProfile(profileHistoryState)
  }

  const runReverseRoute = async () => {
    if (isCalculatingRoute.value) return

    try {
      isCalculatingRoute.value = true

      const editingContext = getEditingTrackContext()
      if (editingContext) {
        setSpinningRoutePoints(
          getTrackRoutePoints(editingContext.tour.routePoints, editingContext.trackClientId),
        )
        await reverseEditingTrackRoute()
        return
      }

      const drawingContext = getDrawingTrackContext()
      if (!drawingContext) return
      setSpinningRoutePoints(
        getTrackRoutePoints(drawingContext.tour.routePoints, drawingContext.trackClientId),
      )
      await reverseDrawingTrackRoute()
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Failed to reverse route', { error })
      showError(
        `Failed to reverse route: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const runProfileReroute = async (profileHistoryState: ProfileRerouteHistoryState) => {
    if (isCalculatingRoute.value) {
      pendingProfileReroute.value = pendingProfileReroute.value
        ? {
            previousProfile: pendingProfileReroute.value.previousProfile,
            nextProfile: profileHistoryState.nextProfile,
          }
        : profileHistoryState
      return
    }

    pendingProfileReroute.value = null

    try {
      const resolveProfileRerouteSpinnerPoints = () => {
        const editingContext = getEditingTrackContext()
        if (editingContext) {
          return getTrackRoutePoints(editingContext.tour.routePoints, editingContext.trackClientId)
        }

        const drawingContext = getDrawingTrackContext()
        if (!drawingContext) return []

        return getTrackRoutePoints(drawingContext.tour.routePoints, drawingContext.trackClientId)
      }

      isCalculatingRoute.value = true
      setSpinningRoutePoints(resolveProfileRerouteSpinnerPoints())
      await rerouteActiveTrackForCurrentProfile(profileHistoryState)
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Failed to reroute after profile change', { error })
      showError(
        `Failed to update route for the selected profile: ${error instanceof Error ? error.message : String(error)}`,
      )
    } finally {
      isCalculatingRoute.value = false
      clearSpinningRoutePoints()
    }
  }

  const shouldSkipProfileRerouteForSparseTrackSetup = () => {
    const currentSetup = sparseTrackSetup.value
    if (!currentSetup) return false

    const context = getEditingTrackContext()
    if (!context) return false
    if (
      context.tourId !== currentSetup.tourId ||
      context.trackClientId !== currentSetup.trackClientId
    ) {
      return false
    }

    const trackRoutePoints = getTrackRoutePoints(context.tour.routePoints, context.trackClientId)
    return trackRoutePoints.length <= 2
  }

  const stopRoutingProfileWatcher = watch(
    () => uiStore.routingProfile,
    (nextProfile, previousProfile) => {
      if (nextProfile === previousProfile) return
      syncDraftTrackRoutingProfile(nextProfile)
      if (isApplyingProfileHistory.value) return
      if (!uiStore.isTrackEditMode && !getDrawingTrackContext()) return
      if (shouldSkipProfileRerouteForSparseTrackSetup()) return
      void runProfileReroute({ previousProfile, nextProfile })
    },
    { flush: 'sync' },
  )

  const stopPendingProfileRerouteWatcher = watch(
    isCalculatingRoute,
    (calculating) => {
      if (calculating || !pendingProfileReroute.value) return
      if (!uiStore.isTrackEditMode && !getDrawingTrackContext()) {
        pendingProfileReroute.value = null
        return
      }
      const profileHistoryState = pendingProfileReroute.value
      pendingProfileReroute.value = null
      void runProfileReroute(profileHistoryState)
    },
    { flush: 'sync' },
  )

  const dispose = () => {
    stopPendingProfileRerouteWatcher()
    stopRoutingProfileWatcher()
    stopAppendDirectionWatcher()
    unregisterSessionHandler()
    clearSpinningRoutePoints()
  }

  if (getCurrentScope()) {
    onScopeDispose(dispose)
  }

  return {
    activeDraftTour: getActiveRouteDraftTour,
    isRouteDraftDirty,
    drawing: {
      isTrackDragging,
      shouldSuppressDragClick,
      trackClientId: drawingTrackClientId,
      pendingRoutePoints: drawingPendingRoutePoints,
      canReverseTrackDrawing,
      canUndoTrackDrawing,
      canRedoTrackDrawing,
      canSaveTrackDrawing,
      beginDrawing,
      cancelTrackDragging,
      cancelTrackDrawing,
      discardTrackDrawing,
      closeTrackDrawing,
      saveTrackDrawing,
      undoLastSegment: () => executeDrawingHistory('undo'),
      redoLastSegment: () => executeDrawingHistory('redo'),
      reverseTrackDrawingRoute: runReverseRoute,
      reorderTrackDrawingRoute: runReorderRoute,
      addDrawingRoutePoint,
      handleDrawingClick,
      confirmTrackDrawing: saveTrackDrawing,
    },
    edit: {
      editModeNewSegments,
      editModeHasRoutePointChanges,
      pendingRoutePoints: drawingPendingRoutePreviewPoints,
      sparseTrackSetup,
      isSparseTrackSetupOpen,
      isApplyingSparseTrackSetup,
      sparseTrackPreviewRoutePoints,
      canReverseTrackEdit,
      canUndoTrackEdit,
      canRedoTrackEdit,
      canSaveTrackEdit,
      markRoutePointChanged: () => undefined,
      resetEditState,
      initEditModeLastClickedPoint,
      cancelTrackEdit,
      discardTrackEdit,
      closeTrackEdit,
      saveTrackEdit,
      confirmTrackEdit: saveTrackEdit,
      maybeOpenSparseTrackSetup,
      closeSparseTrackSetup,
      updateSparseTrackPointCount,
      confirmSparseTrackSetup,
      handleTrackEditClick,
      handleRoutePointDragend,
      handleTrackEditInsertDrop,
      reverseTrackEditRoute: runReverseRoute,
      reorderTrackEditRoute: runReorderRoute,
      undoEditModeSegment: () => executeEditHistory('undo'),
      redoTrackEdit: () => executeEditHistory('redo'),
      handlePendingDeleted,
      handlePendingDragend,
      justEnteredEditMode,
      markJustEnteredEditMode,
    },
  }
}
